import { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Labor rate per hour (for quoted price)
const LABOR_RATE = 140.00;
// Standard cost rate per hour (for dealer cost) - 79.64% margin
const COST_RATE = 28.50;
// Quality Checks cost rate per hour - 51.43% margin
const QUALITY_CHECKS_COST_RATE = 68.00;

// ===================
// AUTH CONTEXT
// ===================
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// ===================
// THEME CONTEXT
// ===================
const ThemeContext = createContext(null);

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  // Set axios default header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('auth_token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('auth_token');
    }
  }, [token]);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/check`);
        if (response.data.authenticated) {
          setUser(response.data.user);
        } else {
          setToken(null);
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [token]);

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// ===================
// LOGIN PAGE
// ===================
const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(username, password);
      } else {
        await register({ username, password, email, full_name: fullName });
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo/Title */}
          <div className="text-center mb-8">
            <div className="text-4xl mb-2">✈️</div>
            <h1 className="text-2xl font-bold text-slate-800">Avionics Quote Tool</h1>
            <p className="text-slate-500 mt-1">{isLogin ? 'Sign in to your account' : 'Create a new account'}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4" data-testid="login-error">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Enter username"
                required
                data-testid="login-username"
              />
            </div>

            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="Enter email"
                    required
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="Enter full name"
                    data-testid="register-fullname"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Enter password"
                required
                data-testid="login-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-600 text-white py-2 px-4 rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {/* Toggle Login/Register */}
          <div className="mt-6 text-center">
            <button
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-cyan-600 hover:text-cyan-800 text-sm"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>

          {/* Default credentials hint */}
          {isLogin && (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <p className="font-medium">Default credentials:</p>
              <p>Username: <code className="bg-slate-200 px-1 rounded">admin</code></p>
              <p>Password: <code className="bg-slate-200 px-1 rounded">admin123</code></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===================
// QUICK SEARCH COMPONENT
// ===================
const QuickSearch = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ quotes: [], products: [], customers: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useCallback(node => { if (node) node.focus(); }, []);

  useEffect(() => {
    const search = async () => {
      if (query.length < 2) {
        setResults({ quotes: [], products: [], customers: [] });
        return;
      }
      
      setLoading(true);
      try {
        const [quotesRes, productsRes, customersRes] = await Promise.all([
          axios.get(`${API}/quotations`),
          axios.get(`${API}/products`),
          axios.get(`${API}/customers`)
        ]);
        
        const q = query.toLowerCase();
        const quotes = quotesRes.data.filter(item => 
          item.quote_number?.toLowerCase().includes(q) ||
          item.party_name?.toLowerCase().includes(q)
        ).slice(0, 5);
        
        const products = productsRes.data.filter(item =>
          item.product_name?.toLowerCase().includes(q) ||
          item.part_number?.toLowerCase().includes(q)
        ).slice(0, 5);
        
        const customers = customersRes.data.filter(item =>
          item.name?.toLowerCase().includes(q) ||
          item.company?.toLowerCase().includes(q)
        ).slice(0, 5);
        
        setResults({ quotes, products, customers });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };
    
    const debounce = setTimeout(search, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const handleSelect = (type, item) => {
    onClose();
    if (type === 'quote') navigate(`/quotations/${item.id}`);
    else if (type === 'product') navigate('/products');
    else if (type === 'customer') navigate('/customers');
  };

  const totalResults = results.quotes.length + results.products.length + results.customers.length;

  return (
    <div className="fixed inset-0 z-50 search-overlay bg-black/50" onClick={onClose}>
      <div className="flex items-start justify-center pt-20 px-4" onClick={e => e.stopPropagation()}>
        <div className="w-full max-w-2xl bg-white dark:bg-slate-800 rounded-xl shadow-2xl overflow-hidden" style={{ backgroundColor: 'var(--bg-card)' }}>
          <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search quotes, products, customers... (Esc to close)"
                className="flex-1 text-lg outline-none bg-transparent"
                style={{ color: 'var(--text-primary)' }}
                data-testid="quick-search-input"
              />
              <span className="kbd">Esc</span>
            </div>
          </div>
          
          <div className="max-h-96 overflow-y-auto p-2">
            {loading ? (
              <div className="p-4 text-center text-slate-500">Searching...</div>
            ) : query.length < 2 ? (
              <div className="p-4 text-center text-slate-500">Type at least 2 characters to search</div>
            ) : totalResults === 0 ? (
              <div className="p-4 text-center text-slate-500">No results found</div>
            ) : (
              <>
                {results.quotes.length > 0 && (
                  <div className="mb-2">
                    <div className="px-3 py-1 text-xs font-medium text-slate-500 uppercase">Quotes</div>
                    {results.quotes.map(q => (
                      <button
                        key={q.id}
                        onClick={() => handleSelect('quote', q)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        <span className="text-xl">📋</span>
                        <div>
                          <div className="font-medium">{q.quote_number}</div>
                          <div className="text-sm text-slate-500">{q.party_name} • ${q.total_amount?.toLocaleString()}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {results.products.length > 0 && (
                  <div className="mb-2">
                    <div className="px-3 py-1 text-xs font-medium text-slate-500 uppercase">Products</div>
                    {results.products.map(p => (
                      <button
                        key={p.id}
                        onClick={() => handleSelect('product', p)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        <span className="text-xl">🔧</span>
                        <div>
                          <div className="font-medium">{p.product_name?.substring(0, 50)}</div>
                          <div className="text-sm text-slate-500">{p.part_number} • ${p.list_price?.toLocaleString()}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {results.customers.length > 0 && (
                  <div>
                    <div className="px-3 py-1 text-xs font-medium text-slate-500 uppercase">Customers</div>
                    {results.customers.map(c => (
                      <button
                        key={c.id}
                        onClick={() => handleSelect('customer', c)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        <span className="text-xl">👤</span>
                        <div>
                          <div className="font-medium">{c.name}</div>
                          <div className="text-sm text-slate-500">{c.company || c.email}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="p-3 border-t text-xs text-slate-500 flex gap-4" style={{ borderColor: 'var(--border-color)' }}>
            <span><span className="kbd">↑↓</span> Navigate</span>
            <span><span className="kbd">Enter</span> Select</span>
            <span><span className="kbd">Esc</span> Close</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================
// LAYOUT COMPONENT
// ===================
const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showQuickSearch, setShowQuickSearch] = useState(false);
  const isQuoteFormPage = location.pathname.includes('/quotations/new') || 
                          (location.pathname.includes('/quotations/') && location.pathname !== '/quotations');
  
  const [sidebarOpen, setSidebarOpen] = useState(!isQuoteFormPage);

  // Auto-collapse sidebar when entering quote form pages
  useEffect(() => {
    if (isQuoteFormPage) {
      setSidebarOpen(false);
    }
  }, [isQuoteFormPage]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + K for Quick Search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowQuickSearch(prev => !prev);
      }
      // Ctrl/Cmd + D for toggle Dark mode
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        toggleTheme();
      }
      // Escape to close quick search
      if (e.key === 'Escape' && showQuickSearch) {
        setShowQuickSearch(false);
      }
      // Ctrl/Cmd + N for new quote
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        navigate('/quotations/new');
      }
      // G then H for go home (dashboard)
      if (e.key === 'g' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const handleNextKey = (nextE) => {
          if (nextE.key === 'h') {
            navigate('/');
          } else if (nextE.key === 'q') {
            navigate('/quotations');
          } else if (nextE.key === 'p') {
            navigate('/products');
          } else if (nextE.key === 'c') {
            navigate('/customers');
          }
          document.removeEventListener('keydown', handleNextKey);
        };
        setTimeout(() => document.addEventListener('keydown', handleNextKey, { once: true }), 0);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showQuickSearch, toggleTheme, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Quick Search Modal */}
      {showQuickSearch && <QuickSearch onClose={() => setShowQuickSearch(false)} />}
      
      {/* Sidebar */}
      <aside 
        className={`${sidebarOpen ? 'w-64' : 'w-16'} text-white transition-all duration-300 flex flex-col flex-shrink-0`}
        style={{ backgroundColor: 'var(--bg-sidebar)' }}
      >
        <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div>
                <h1 className="text-xl font-bold text-cyan-400">QuoteTool</h1>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Avionics Maintenance</p>
              </div>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-white/10 rounded" data-testid="sidebar-toggle">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarOpen ? "M11 19l-7-7 7-7" : "M13 5l7 7-7 7"} />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Quick Search Button */}
        <div className="px-4 pt-4">
          <button
            onClick={() => setShowQuickSearch(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors hover:bg-white/10"
            style={{ backgroundColor: 'var(--bg-hover)', color: 'var(--text-secondary)' }}
            data-testid="quick-search-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {sidebarOpen && (
              <>
                <span className="flex-1 text-left text-sm">Search...</span>
                <span className="kbd text-xs">⌘K</span>
              </>
            )}
          </button>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <NavItem to="/" icon="🏠" label="Dashboard" expanded={sidebarOpen} />
          <NavItem to="/quotations" icon="📋" label="Quotations" expanded={sidebarOpen} />
          <NavItem to="/customers" icon="👥" label="Customers" expanded={sidebarOpen} />
          <NavItem to="/products" icon="🔧" label="Products" expanded={sidebarOpen} />
          <NavItem to="/tasks" icon="⚙️" label="Labor Tasks" expanded={sidebarOpen} />
          <NavItem to="/templates" icon="📑" label="Templates" expanded={sidebarOpen} />
          <NavItem to="/import-prices" icon="💰" label="Price Import" expanded={sidebarOpen} />
          <NavItem to="/audit-logs" icon="📜" label="Audit Logs" expanded={sidebarOpen} />
          <NavItem to="/backups" icon="💾" label="Backups" expanded={sidebarOpen} />
        </nav>
        
        {/* Theme Toggle & User Info */}
        <div className="p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-2 px-3 py-2 mb-3 rounded-lg transition-colors hover:bg-white/10"
            style={{ color: 'var(--text-secondary)' }}
            data-testid="theme-toggle-btn"
            title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
          >
            {theme === 'light' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            )}
            {sidebarOpen && (
              <>
                <span className="flex-1 text-left text-sm">{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
                <span className="kbd text-xs">⌘D</span>
              </>
            )}
          </button>
          
          {/* User Info */}
          {sidebarOpen ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center text-sm font-bold">
                  {user?.username?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{user?.full_name || user?.username}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors hover:bg-white/10"
                style={{ color: 'var(--text-secondary)' }}
                data-testid="logout-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center p-2 rounded-lg transition-colors hover:bg-white/10"
              style={{ color: 'var(--text-secondary)' }}
              title="Sign Out"
              data-testid="logout-btn-collapsed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

const NavItem = ({ to, icon, label, expanded }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
        isActive ? 'bg-cyan-600 text-white' : 'hover:bg-white/10'
      }`
    }
    style={{ color: 'var(--text-sidebar)' }}
    data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
  >
    <span className="text-xl">{icon}</span>
    {expanded && <span>{label}</span>}
  </NavLink>
);

// ===================
// DASHBOARD
// ===================
const Dashboard = () => {
  const [stats, setStats] = useState({ total_quotes: 0, total_products: 0, total_tasks: 0, total_revenue: 0 });
  const [recentQuotes, setRecentQuotes] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, quotesRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/quotations`)
        ]);
        setStats(statsRes.data);
        setRecentQuotes(quotesRes.data.slice(0, 5));
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      }
    };
    fetchData();
  }, []);

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6" style={{ color: 'var(--text-primary)' }} data-testid="dashboard-title">Dashboard</h1>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatCard title="Total Quotes" value={stats.total_quotes} icon="📋" color="bg-blue-500" />
        <StatCard title="Products" value={stats.total_products} icon="🔧" color="bg-green-500" />
        <StatCard title="Labor Tasks" value={stats.total_tasks} icon="⚙️" color="bg-purple-500" />
        <StatCard title="Total Revenue" value={`$${stats.total_revenue.toLocaleString()}`} icon="💰" color="bg-cyan-500" />
      </div>

      {/* Recent Quotes */}
      <div className="rounded-xl shadow-lg p-6" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Recent Quotations</h2>
          <NavLink to="/quotations/new" className="btn-primary" data-testid="new-quote-btn">
            + New Quote
          </NavLink>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="recent-quotes-table">
            <thead style={{ backgroundColor: 'var(--bg-hover)' }}>
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Quote #</th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Customer</th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Date</th>
                <th className="px-4 py-3 text-right text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Total</th>
                <th className="px-4 py-3 text-center text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {recentQuotes.map(quote => (
                <tr key={quote.id} className="hover:opacity-80" style={{ borderColor: 'var(--border-color)' }}>
                  <td className="px-4 py-3">
                    <NavLink to={`/quotations/${quote.id}`} className="text-cyan-500 hover:underline">
                      {quote.quote_number}
                    </NavLink>
                  </td>
                  <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{quote.party_name}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-muted)' }}>{new Date(quote.quote_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>${quote.total_amount.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      quote.status === 'Approved' ? 'bg-green-100 text-green-700' :
                      quote.status === 'Sent' ? 'bg-blue-100 text-blue-700' :
                      quote.status === 'Expired' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {quote.status}
                    </span>
                  </td>
                </tr>
              ))}
              {recentQuotes.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center" style={{ color: 'var(--text-muted)' }}>No quotations yet. Create your first quote!</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color }) => (
  <div className="rounded-xl shadow-lg p-6 flex items-center gap-4" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
    <div className={`${color} text-white p-4 rounded-xl text-2xl`}>{icon}</div>
    <div>
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{title}</p>
      <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{value}</p>
    </div>
  </div>
);

// ===================
// SEARCH & PAGINATION COMPONENTS
// ===================
const SearchBar = ({ value, onChange, placeholder = "Search..." }) => (
  <div className="relative">
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="form-input pl-10 pr-4 py-2 w-full md:w-80"
      data-testid="search-input"
    />
    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    {value && (
      <button 
        onClick={() => onChange('')}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        data-testid="clear-search"
      >
        ✕
      </button>
    )}
  </div>
);

const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let start = Math.max(1, currentPage - Math.floor(showPages / 2));
    let end = Math.min(totalPages, start + showPages - 1);
    
    if (end - start + 1 < showPages) {
      start = Math.max(1, end - showPages + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-4 px-4 py-3 bg-slate-50 rounded-lg">
      <div className="text-sm text-slate-600">
        Showing <span className="font-medium">{startItem}</span> to <span className="font-medium">{endItem}</span> of <span className="font-medium">{totalItems}</span> results
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="px-2 py-1 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 text-slate-600"
          data-testid="pagination-first"
        >
          ««
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 text-slate-600"
          data-testid="pagination-prev"
        >
          ‹ Prev
        </button>
        {getPageNumbers().map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1 rounded text-sm font-medium ${
              currentPage === page 
                ? 'bg-cyan-600 text-white' 
                : 'hover:bg-slate-200 text-slate-600'
            }`}
            data-testid={`pagination-page-${page}`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 text-slate-600"
          data-testid="pagination-next"
        >
          Next ›
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="px-2 py-1 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 text-slate-600"
          data-testid="pagination-last"
        >
          »»
        </button>
      </div>
    </div>
  );
};

const ItemsPerPageSelect = ({ value, onChange }) => (
  <div className="flex items-center gap-2 text-sm text-slate-600">
    <span>Show</span>
    <select 
      value={value} 
      onChange={(e) => onChange(Number(e.target.value))}
      className="form-select py-1 px-2 text-sm rounded"
      data-testid="items-per-page"
    >
      <option value={10}>10</option>
      <option value={25}>25</option>
      <option value={50}>50</option>
      <option value={100}>100</option>
    </select>
    <span>per page</span>
  </div>
);

// ===================
// AI QUOTE ASSISTANT
// ===================
const AIQuoteAssistant = ({ onAddItems, onClose, aircraftId, aircraftMake, aircraftModel }) => {
  const [message, setMessage] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [clarification, setClarification] = useState('');
  const [selectedItems, setSelectedItems] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    setLoading(true);
    setClarification('');
    setMatches([]);
    
    try {
      const response = await axios.post(`${API}/ai/parse-quote`, {
        message: message,
        customer_name: customerName,
        aircraft_id: aircraftId || '',
        aircraft_make: aircraftMake || '',
        aircraft_model: aircraftModel || ''
      });
      
      if (response.data.success) {
        setMatches(response.data.matches);
        if (response.data.extracted_customer) {
          setCustomerName(response.data.extracted_customer);
        }
        if (response.data.clarification_needed) {
          setClarification(response.data.clarification_message);
        }
        // Auto-select high confidence items
        const autoSelected = {};
        response.data.matches.forEach((m, i) => {
          if (m.confidence === 'high') {
            autoSelected[i] = { selected: true, item: m };
          }
        });
        setSelectedItems(autoSelected);
      } else {
        setClarification(response.data.clarification_message || 'Sorry, something went wrong.');
      }
    } catch (err) {
      setClarification('Error connecting to AI service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (index, match, alternativeId = null) => {
    if (alternativeId) {
      // Select an alternative
      const alt = match.alternatives.find(a => a.id === alternativeId);
      if (alt) {
        setSelectedItems(prev => ({
          ...prev,
          [index]: { 
            selected: true, 
            item: { ...match, id: alt.id, name: alt.name, part_number: alt.part_number || '', price: alt.price || 0, hours: alt.hours || 0 }
          }
        }));
      }
    } else {
      setSelectedItems(prev => ({
        ...prev,
        [index]: prev[index]?.selected ? { selected: false } : { selected: true, item: match }
      }));
    }
  };

  const handleAddToQuote = () => {
    const itemsToAdd = Object.values(selectedItems)
      .filter(s => s.selected && s.item)
      .map(s => s.item);
    onAddItems(itemsToAdd, customerName);
  };

  const getConfidenceBadge = (confidence) => {
    const styles = {
      high: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-red-100 text-red-700'
    };
    const labels = {
      high: '✓ Exact Match',
      medium: '? Review',
      low: '⚠ Low Match'
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${styles[confidence]}`}>{labels[confidence]}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">AI Quote Assistant</h2>
            <p className="text-purple-200 text-sm">
              {aircraftMake && aircraftModel 
                ? `✈️ Finding products for ${aircraftMake} ${aircraftModel}`
                : 'Describe what you need in natural language'
              }
            </p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">&times;</button>
        </div>
        
        <div className="p-4 flex-1 overflow-y-auto">
          {aircraftMake && aircraftModel && (
            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center gap-2">
              <span className="text-2xl">✈️</span>
              <div>
                <p className="font-medium text-indigo-800">{aircraftMake} {aircraftModel}</p>
                <p className="text-xs text-indigo-600">Products will be filtered for this aircraft</p>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Customer Name (optional)</label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="e.g., John Smith"
                className="form-input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">What do you need?</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={aircraftMake ? `e.g., GFC 500 install with G5 for ${aircraftMake}` : "e.g., GTN 650Xi with G5 installation"}
                className="form-input w-full h-24 resize-none"
                data-testid="ai-quote-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !message.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="ai-quote-submit"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Analyzing...
                </>
              ) : (
                <>🤖 Find Products & Tasks</>
              )}
            </button>
          </form>
          
          {clarification && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800">{clarification}</p>
            </div>
          )}
          
          {matches.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="font-semibold text-slate-800">Found Items:</h3>
              
              {matches.map((match, index) => (
                <div 
                  key={index} 
                  className={`p-4 border rounded-lg transition-all ${
                    selectedItems[index]?.selected 
                      ? 'border-cyan-500 bg-cyan-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedItems[index]?.selected || false}
                      onChange={() => toggleItem(index, match)}
                      className="mt-1 w-5 h-5 text-cyan-600 rounded cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          match.type === 'product' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {match.type === 'product' ? '📦 Product' : '⚙️ Labor'}
                        </span>
                        {getConfidenceBadge(match.confidence)}
                      </div>
                      <p className="font-medium text-slate-800 mt-1">{match.name}</p>
                      {match.part_number && (
                        <p className="text-sm text-slate-500">Part #: {match.part_number}</p>
                      )}
                      <div className="flex gap-4 mt-1 text-sm">
                        {match.type === 'product' && (
                          <span className="text-green-600 font-medium">${match.price.toLocaleString()}</span>
                        )}
                        {match.hours > 0 && (
                          <span className="text-slate-600">{match.hours} hours</span>
                        )}
                      </div>
                      
                      {/* Alternatives for medium/low confidence */}
                      {match.alternatives && match.alternatives.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500 mb-2">Did you mean one of these?</p>
                          <div className="space-y-2">
                            {match.alternatives.map((alt) => (
                              <button
                                key={alt.id}
                                onClick={() => toggleItem(index, match, alt.id)}
                                className="w-full text-left p-2 bg-slate-50 hover:bg-slate-100 rounded text-sm transition-colors"
                              >
                                <span className="font-medium">{alt.name}</span>
                                {alt.part_number && <span className="text-slate-500 ml-2">({alt.part_number})</span>}
                                {alt.price > 0 && <span className="text-green-600 ml-2">${alt.price.toLocaleString()}</span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {matches.length > 0 && (
          <div className="p-4 bg-slate-50 border-t flex justify-between items-center">
            <span className="text-sm text-slate-600">
              {Object.values(selectedItems).filter(s => s.selected).length} item(s) selected
            </span>
            <div className="flex gap-3">
              <button onClick={onClose} className="btn-secondary">Cancel</button>
              <button 
                onClick={handleAddToQuote}
                disabled={Object.values(selectedItems).filter(s => s.selected).length === 0}
                className="btn-primary"
                data-testid="ai-add-to-quote"
              >
                Add to Quote
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===================
// PRODUCTS
// ===================
const Products = () => {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({ product_name: '', part_number: '', dealer_cost: 0, list_price: 0, notes: '' });
  
  // Search & Pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const fetchProducts = useCallback(async () => {
    const res = await axios.get(`${API}/products`);
    setProducts(res.data);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Filter products based on search
  const filteredProducts = products.filter(product => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (product.product_name || '').toLowerCase().includes(searchLower) ||
      (product.part_number || '').toLowerCase().includes(searchLower)
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editProduct) {
        await axios.put(`${API}/products/${editProduct.id}`, form);
      } else {
        await axios.post(`${API}/products`, form);
      }
      setShowModal(false);
      setForm({ product_name: '', part_number: '', dealer_cost: 0, list_price: 0, notes: '' });
      setEditProduct(null);
      fetchProducts();
    } catch (err) {
      alert('Error saving product');
    }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setForm(product);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this product?')) {
      await axios.delete(`${API}/products/${id}`);
      fetchProducts();
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }} data-testid="products-title">Products / Parts</h1>
        <button onClick={() => { setEditProduct(null); setForm({ product_name: '', part_number: '', dealer_cost: 0, list_price: 0, notes: '' }); setShowModal(true); }} className="btn-primary" data-testid="add-product-btn">
          + Add Product
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="rounded-xl shadow-lg p-4 mb-4" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <SearchBar 
            value={searchTerm} 
            onChange={setSearchTerm} 
            placeholder="Search by name or part number..."
          />
          <div className="flex items-center gap-4">
            <ItemsPerPageSelect value={itemsPerPage} onChange={setItemsPerPage} />
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-xl shadow-lg overflow-hidden" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <table className="w-full" data-testid="products-table">
          <thead className="bg-slate-800 text-white">
            <tr>
              <th className="px-4 py-3 text-left">Product Name</th>
              <th className="px-4 py-3 text-left">Part Number</th>
              <th className="px-4 py-3 text-right">Dealer Cost</th>
              <th className="px-4 py-3 text-right">List Price</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
            {paginatedProducts.map(product => (
              <tr key={product.id} className="transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                <td className="px-4 py-3 font-medium" style={{ color: 'var(--text-primary)' }}>{product.product_name}</td>
                <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{product.part_number}</td>
                <td className="px-4 py-3 text-right" style={{ color: 'var(--text-primary)' }}>${product.dealer_cost.toFixed(2)}</td>
                <td className="px-4 py-3 text-right" style={{ color: 'var(--text-primary)' }}>${product.list_price.toFixed(2)}</td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => handleEdit(product)} className="text-cyan-500 hover:text-cyan-400 mr-3">Edit</button>
                  <button onClick={() => handleDelete(product.id)} className="text-red-500 hover:text-red-400">Delete</button>
                </td>
              </tr>
            ))}
            {paginatedProducts.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center" style={{ color: 'var(--text-muted)' }}>
                  {searchTerm ? 'No products match your search.' : 'No products found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredProducts.length}
          itemsPerPage={itemsPerPage}
        />
      </div>

      {showModal && (
        <Modal title={editProduct ? 'Edit Product' : 'Add Product'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input label="Product Name" value={form.product_name} onChange={e => setForm({...form, product_name: e.target.value})} required />
            <Input label="Part Number" value={form.part_number} onChange={e => setForm({...form, part_number: e.target.value})} />
            <div className="grid grid-cols-2 gap-4">
              <Input label="Dealer Cost" type="number" step="0.01" value={form.dealer_cost} onChange={e => setForm({...form, dealer_cost: parseFloat(e.target.value) || 0})} />
              <Input label="List Price" type="number" step="0.01" value={form.list_price} onChange={e => setForm({...form, list_price: parseFloat(e.target.value) || 0})} />
            </div>
            <Input label="Notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} textarea />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-product-btn">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ===================
// TASKS
// ===================
const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [form, setForm] = useState({ task_description: '', estimated_hours: 0 });
  
  // Search & Pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const fetchTasks = useCallback(async () => {
    const res = await axios.get(`${API}/tasks`);
    setTasks(res.data);
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Filter tasks based on search
  const filteredTasks = tasks.filter(task => {
    const searchLower = searchTerm.toLowerCase();
    return (task.task_description || '').toLowerCase().includes(searchLower);
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);
  const paginatedTasks = filteredTasks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editTask) {
        await axios.put(`${API}/tasks/${editTask.id}`, form);
      } else {
        await axios.post(`${API}/tasks`, form);
      }
      setShowModal(false);
      setForm({ task_description: '', estimated_hours: 0 });
      setEditTask(null);
      fetchTasks();
    } catch (err) {
      alert('Error saving task');
    }
  };

  const handleEdit = (task) => {
    setEditTask(task);
    setForm(task);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this task?')) {
      await axios.delete(`${API}/tasks/${id}`);
      fetchTasks();
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-800" data-testid="tasks-title">Labor Tasks</h1>
        <button onClick={() => { setEditTask(null); setForm({ task_description: '', estimated_hours: 0 }); setShowModal(true); }} className="btn-primary" data-testid="add-task-btn">
          + Add Task
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <SearchBar 
            value={searchTerm} 
            onChange={setSearchTerm} 
            placeholder="Search tasks..."
          />
          <div className="flex items-center gap-4">
            <ItemsPerPageSelect value={itemsPerPage} onChange={setItemsPerPage} />
            <span className="text-sm text-slate-500">
              {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <table className="w-full" data-testid="tasks-table">
          <thead className="bg-slate-800 text-white">
            <tr>
              <th className="px-4 py-3 text-left">Task Description</th>
              <th className="px-4 py-3 text-right">Est. Hours</th>
              <th className="px-4 py-3 text-right">Labor Cost ($140/hr)</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedTasks.map(task => (
              <tr key={task.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{task.task_description}</td>
                <td className="px-4 py-3 text-right">{task.estimated_hours}</td>
                <td className="px-4 py-3 text-right">${(task.estimated_hours * LABOR_RATE).toFixed(2)}</td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => handleEdit(task)} className="text-cyan-600 hover:text-cyan-800 mr-3">Edit</button>
                  <button onClick={() => handleDelete(task.id)} className="text-red-600 hover:text-red-800">Delete</button>
                </td>
              </tr>
            ))}
            {paginatedTasks.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  {searchTerm ? 'No tasks match your search.' : 'No tasks found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredTasks.length}
          itemsPerPage={itemsPerPage}
        />
      </div>

      {showModal && (
        <Modal title={editTask ? 'Edit Task' : 'Add Task'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input label="Task Description" value={form.task_description} onChange={e => setForm({...form, task_description: e.target.value})} required />
            <Input label="Estimated Hours" type="number" step="0.5" value={form.estimated_hours} onChange={e => setForm({...form, estimated_hours: parseFloat(e.target.value) || 0})} />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-task-btn">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ===================
// QUOTATIONS LIST
// ===================
const Quotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState({});
  const [pandadocTemplates, setPandadocTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const navigate = useNavigate();
  
  // Search, Filter & Pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const fetchQuotations = useCallback(async () => {
    const res = await axios.get(`${API}/quotations`);
    setQuotations(res.data);
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pandadoc/templates`);
      setPandadocTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, []);

  useEffect(() => {
    fetchQuotations();
    fetchTemplates();
  }, [fetchQuotations, fetchTemplates]);

  // Filter quotations based on search and status
  const filteredQuotations = quotations.filter(quote => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      (quote.quote_number || '').toLowerCase().includes(searchLower) ||
      (quote.party_name || '').toLowerCase().includes(searchLower)
    );
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Get unique statuses for filter dropdown
  const uniqueStatuses = [...new Set(quotations.map(q => q.status).filter(Boolean))];

  // Pagination calculations
  const totalPages = Math.ceil(filteredQuotations.length / itemsPerPage);
  const paginatedQuotations = filteredQuotations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, itemsPerPage]);

  const handleDelete = async (id) => {
    if (window.confirm('Delete this quotation?')) {
      await axios.delete(`${API}/quotations/${id}`);
      setQuotations(quotations.filter(q => q.id !== id));
    }
  };

  const handleClone = async (id, quoteNumber) => {
    if (window.confirm(`Clone quote ${quoteNumber}? This will create a new quote with the same items.`)) {
      try {
        const response = await axios.post(`${API}/quotations/${id}/clone`);
        alert(`Quote cloned! New quote number: ${response.data.quote_number}`);
        fetchQuotations();
        navigate(`/quotations/${response.data.id}`);
      } catch (error) {
        alert('Error cloning quote');
      }
    }
  };

  const handleExtend = async (id, quoteNumber) => {
    if (window.confirm(`Extend quote ${quoteNumber} by 60 days?`)) {
      try {
        await axios.put(`${API}/quotations/${id}/extend`, null, { params: { days: 60 } });
        alert('Quote expiration extended!');
        fetchQuotations();
      } catch (error) {
        alert('Error extending quote');
      }
    }
  };

  const getExpirationStatus = (quote) => {
    if (!quote.expiration_date) return null;
    if (quote.status === 'Expired') return { status: 'expired', text: 'Expired', color: 'bg-red-100 text-red-700' };
    
    const expDate = new Date(quote.expiration_date);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return { status: 'expired', text: 'Expired', color: 'bg-red-100 text-red-700' };
    if (daysUntilExpiry <= 7) return { status: 'expiring', text: `${daysUntilExpiry}d left`, color: 'bg-amber-100 text-amber-700' };
    return { status: 'valid', text: `${daysUntilExpiry}d`, color: 'bg-green-100 text-green-700' };
  };

  const handleDownloadPDF = async (id, quoteNumber) => {
    const response = await axios.get(`${API}/quotations/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `quote_${quoteNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openTemplateModal = (quoteId) => {
    setSelectedQuoteId(quoteId);
    setShowTemplateModal(true);
  };

  const handleCreatePandaDoc = async (quoteId) => {
    if (!quoteId) return;
    
    setLoading(prev => ({ ...prev, [quoteId]: 'creating' }));
    try {
      const response = await axios.post(`${API}/quotations/${quoteId}/pandadoc/create`);
      alert(`PandaDoc draft created!\nDocument: ${response.data.document_name}\n\nOpen PandaDoc to review and send.`);
      fetchQuotations();
    } catch (err) {
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [quoteId]: null }));
    }
  };

  const handleSendPandaDoc = async (id) => {
    setLoading(prev => ({ ...prev, [id]: 'sending' }));
    try {
      await axios.post(`${API}/quotations/${id}/pandadoc/send`);
      alert('Quote sent for signature!');
      fetchQuotations();
    } catch (err) {
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleCheckStatus = async (id) => {
    setLoading(prev => ({ ...prev, [id]: 'checking' }));
    try {
      const response = await axios.get(`${API}/quotations/${id}/pandadoc/status`);
      const status = response.data;
      alert(`PandaDoc Status: ${status.status}\nName: ${status.name || 'N/A'}\nCreated: ${status.date_created || 'N/A'}`);
      fetchQuotations();
    } catch (err) {
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }} data-testid="quotations-title">Quotations</h1>
        <button onClick={() => navigate('/quotations/new')} className="btn-primary" data-testid="new-quotation-btn">
          + New Quotation
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="rounded-xl shadow-lg p-4 mb-4" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <SearchBar 
              value={searchTerm} 
              onChange={setSearchTerm} 
              placeholder="Search by quote # or customer..."
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-select py-2 px-3 rounded-lg text-sm"
              data-testid="status-filter"
            >
              <option value="all">All Statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-4">
            <ItemsPerPageSelect value={itemsPerPage} onChange={setItemsPerPage} />
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {filteredQuotations.length} quote{filteredQuotations.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-xl shadow-lg overflow-hidden" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <table className="w-full" data-testid="quotations-table">
          <thead className="bg-slate-800 text-white">
            <tr>
              <th className="px-4 py-3 text-left">Quote #</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-center">PandaDoc</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
            {paginatedQuotations.map(quote => (
              <tr key={quote.id} className="transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                <td className="px-4 py-3">
                  <NavLink to={`/quotations/${quote.id}`} className="text-cyan-500 hover:underline font-medium">
                    {quote.quote_number}
                  </NavLink>
                  {quote.version > 1 && (
                    <span className="ml-1 text-xs" style={{ color: 'var(--text-muted)' }}>v{quote.version}</span>
                  )}
                </td>
                <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>{quote.party_name}</td>
                <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{new Date(quote.quote_date).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right font-semibold" style={{ color: 'var(--text-primary)' }}>${quote.total_amount?.toLocaleString() || '0'}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      quote.status === 'Approved' || quote.status === 'Won' ? 'bg-green-100 text-green-700' :
                      quote.status === 'Sent' ? 'bg-blue-100 text-blue-700' :
                      quote.status === 'Expired' ? 'bg-red-100 text-red-700' :
                      quote.pandadoc_status === 'document.completed' ? 'bg-green-100 text-green-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {quote.status}
                    </span>
                    {(() => {
                      const expStatus = getExpirationStatus(quote);
                      return expStatus && quote.status !== 'Expired' && quote.status !== 'Won' && quote.status !== 'Accepted' ? (
                        <span className={`px-1.5 py-0.5 rounded text-xs ${expStatus.color}`} title="Expiration">
                          {expStatus.text}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  {loading[quote.id] ? (
                    <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</span>
                  ) : quote.pandadoc_id ? (
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      <span className={`px-2 py-1 rounded text-xs ${
                        quote.pandadoc_status === 'document.completed' ? 'bg-green-100 text-green-700' :
                        quote.pandadoc_status === 'document.sent' ? 'bg-blue-100 text-blue-700' :
                        quote.pandadoc_status === 'document.viewed' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {quote.pandadoc_status?.replace('document.', '') || 'created'}
                      </span>
                      <button onClick={() => handleCheckStatus(quote.id)} className="text-slate-400 hover:text-slate-600 text-xs" title="Refresh status">🔄</button>
                      {quote.pandadoc_status === 'document.draft' && (
                        <a href={`https://app.pandadoc.com/a/#/documents/${quote.pandadoc_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-xs font-medium" title="Open in PandaDoc to review and send">Open</a>
                      )}
                      <button 
                        onClick={() => {
                          if (window.confirm('Create a new PandaDoc? The previous document will remain in PandaDoc but this quote will link to the new one.')) {
                            handleCreatePandaDoc(quote.id);
                          }
                        }} 
                        className="text-purple-600 hover:text-purple-800 text-xs"
                        title="Re-create PandaDoc (for updated quote)"
                        data-testid={`pandadoc-recreate-${quote.id}`}
                      >
                        + New
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={() => handleCreatePandaDoc(quote.id)} 
                      className="text-purple-600 hover:text-purple-800 text-xs font-medium"
                      title="Create PandaDoc"
                      data-testid={`pandadoc-create-${quote.id}`}
                    >
                      + Create
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-center space-x-1">
                  <button onClick={() => navigate(`/quotations/${quote.id}/edit`)} className="text-cyan-600 hover:text-cyan-800" title="Edit">Edit</button>
                  <button onClick={() => handleClone(quote.id, quote.quote_number)} className="text-purple-600 hover:text-purple-800" title="Clone">Clone</button>
                  <button onClick={() => handleDownloadPDF(quote.id, quote.quote_number)} className="text-green-600 hover:text-green-800" title="PDF">PDF</button>
                  {quote.status === 'Expired' && (
                    <button onClick={() => handleExtend(quote.id, quote.quote_number)} className="text-amber-600 hover:text-amber-800" title="Extend 60 days">Extend</button>
                  )}
                  <button onClick={() => handleDelete(quote.id)} className="text-red-600 hover:text-red-800" title="Delete">Del</button>
                </td>
              </tr>
            ))}
            {paginatedQuotations.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  {searchTerm || statusFilter !== 'all' ? 'No quotations match your filters.' : 'No quotations found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredQuotations.length}
          itemsPerPage={itemsPerPage}
        />
      </div>

      {/* PandaDoc Template Selection Modal */}
      {showTemplateModal && (
        <Modal title="Select PandaDoc Template" onClose={() => setShowTemplateModal(false)}>
          <div className="space-y-4">
            <p className="text-slate-600 text-sm">Choose a template for your quote document:</p>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {pandadocTemplates.map(template => (
                <button
                  key={template.id}
                  onClick={() => handleCreatePandaDoc(template.id)}
                  className="w-full text-left p-4 border rounded-lg hover:bg-cyan-50 hover:border-cyan-500 transition-colors"
                >
                  <div className="font-medium text-slate-800">{template.name}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Modified: {template.date_modified ? new Date(template.date_modified).toLocaleDateString() : 'N/A'}
                  </div>
                </button>
              ))}
              {pandadocTemplates.length === 0 && (
                <p className="text-slate-500 text-center py-4">No templates found in PandaDoc account.</p>
              )}
            </div>
            <div className="flex justify-end pt-4 border-t">
              <button onClick={() => setShowTemplateModal(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ===================
// QUOTATION FORM
// ===================
// Service name constants (used for auto-recalculation)
const SVC_AVIONICS = 'Avionics Integration & Config Package';
const SVC_SHOP_SUPPLIES = 'Shop Supplies';
const SVC_WARRANTY = 'Optional 2 year Warranty';

// Round to 2 decimal places (matches backend rounding)
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Build a signature representing user-meaningful form state.
// Auto-calculated service values (Avionics, Shop Supplies, Warranty when applied)
// are EXCLUDED so the auto-recalc effect doesn't cause false-positive "dirty" detection.
const buildSig = (f) => JSON.stringify({
  li: (f.line_items || []).map(li => ({
    pid: li.product_id || '',
    tid: li.task_id || '',
    pn: li.product_name || '',
    td: li.task_description || '',
    qty: Number(li.quantity) || 1,
    mk: Number(li.markup_percentage) || 0,
    hrs: Number(li.quoted_hours) || 0,
    dc: Number(li.dealer_cost) || 0,
    qp: Number(li.quoted_price) || 0,
  })),
  svcs: (f.additional_services || []).map(s => ({
    n: s.name,
    a: !!s.apply,
    qp: (s.is_auto || s.is_percentage) ? null : (Number(s.quoted_price) || 0),
    qh: (s.is_auto || s.is_percentage) ? null : (Number(s.quoted_hours) || 0),
    dc: (s.is_auto || s.is_percentage) ? null : (Number(s.dealer_cost) || 0),
  })),
  at: !!f.apply_tax,
  tr: Number(f.sales_tax_rate) || 0,
});

const QuotationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [products, setProducts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  
  // Aircraft state
  const [aircraftMakes, setAircraftMakes] = useState([]);
  const [aircraftModels, setAircraftModels] = useState([]);
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [showCustomAircraft, setShowCustomAircraft] = useState(false);
  const [customAircraft, setCustomAircraft] = useState({ make: '', model: '', category: 'Custom' });
  const [savingCustomAircraft, setSavingCustomAircraft] = useState(false);
  
  const [form, setForm] = useState({
    party_name: '',
    party_email: '',
    aircraft_info: '',
    aircraft_make: '',
    aircraft_model: '',
    aircraft_id: '',
    line_items: [],
    additional_services: [
      { name: '91.411/91.413 Check/IFR Cert (Optional)', apply: false, quoted_price: 1000.00, dealer_cost: 203.57, quoted_hours: 7.14, profit: 796.43, cost_rate: COST_RATE, use_fixed_price: true },
      { name: 'New Weight & Balance (Optional)', apply: false, quoted_price: 420.00, dealer_cost: 85.50, quoted_hours: 3.0, profit: 334.50, cost_rate: COST_RATE },
      { name: 'Replace Pitot-Static Lines Inside Cabin (Optional)', apply: false, quoted_price: 560.00, dealer_cost: 114.00, quoted_hours: 4.0, profit: 446.00, cost_rate: COST_RATE },
      { name: 'Install Preparation', apply: true, quoted_price: 560.00, dealer_cost: 114.00, quoted_hours: 4.0, profit: 446.00, cost_rate: COST_RATE },
      { name: 'Avionics Integration & Config Package', apply: true, quoted_price: 0, dealer_cost: 0, quoted_hours: 0, profit: 0, cost_rate: COST_RATE, is_auto: true, auto_percentage: 15.0 },
      { name: 'Logbooks, 337, Paperwork, Return to Service, Quality Checks', apply: true, quoted_price: 560.00, dealer_cost: 272.00, quoted_hours: 4.0, profit: 288.00, cost_rate: QUALITY_CHECKS_COST_RATE },
      { name: 'Shop Supplies', apply: true, quoted_price: 0, dealer_cost: 0, quoted_hours: 0, profit: 0, is_percentage: true, is_auto: true, percentage: 2.0 },
      { name: 'Optional 2 year Warranty', apply: false, quoted_price: 0, dealer_cost: 0, quoted_hours: 0, profit: 0, is_percentage: true, percentage: 5.0 },
    ],
    sales_tax_rate: 9.75,
    apply_tax: false,
    description: ''
  });

  // Ref to track if this is initial quote load (to preserve stored service values)
  const isInitialLoad = useRef(true);
  
  // State to store the totals loaded from database (for existing quotes)
  const [storedTotals, setStoredTotals] = useState(null);
  // Signature snapshot of form state at load time. Used to detect whether
  // the user has made any material edit (line items, services, tax).
  const [initialSignature, setInitialSignature] = useState(null);

  // Default services structure with all flags
  const defaultServices = [
    { name: '91.411/91.413 Check/IFR Cert (Optional)', apply: false, quoted_price: 1000.00, dealer_cost: 203.57, quoted_hours: 7.14, profit: 796.43, cost_rate: COST_RATE, use_fixed_price: true },
    { name: 'New Weight & Balance (Optional)', apply: false, quoted_price: 420.00, dealer_cost: 85.50, quoted_hours: 3.0, profit: 334.50, cost_rate: COST_RATE },
    { name: 'Replace Pitot-Static Lines Inside Cabin (Optional)', apply: false, quoted_price: 560.00, dealer_cost: 114.00, quoted_hours: 4.0, profit: 446.00, cost_rate: COST_RATE },
    { name: 'Install Preparation', apply: true, quoted_price: 560.00, dealer_cost: 114.00, quoted_hours: 4.0, profit: 446.00, cost_rate: COST_RATE },
    { name: 'Avionics Integration & Config Package', apply: true, quoted_price: 0, dealer_cost: 0, quoted_hours: 0, profit: 0, cost_rate: COST_RATE, is_auto: true, auto_percentage: 15.0 },
    { name: 'Logbooks, 337, Paperwork, Return to Service, Quality Checks', apply: true, quoted_price: 560.00, dealer_cost: 272.00, quoted_hours: 4.0, profit: 288.00, cost_rate: QUALITY_CHECKS_COST_RATE },
    { name: 'Shop Supplies', apply: true, quoted_price: 0, dealer_cost: 0, quoted_hours: 0, profit: 0, is_percentage: true, is_auto: true, percentage: 2.0 },
    { name: 'Optional 2 year Warranty', apply: false, quoted_price: 0, dealer_cost: 0, quoted_hours: 0, profit: 0, is_percentage: true, percentage: 5.0 },
  ];

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/tasks`),
      axios.get(`${API}/templates`),
      axios.get(`${API}/aircraft/makes`)
    ]).then(([prodRes, taskRes, templRes, aircraftRes]) => {
      setProducts(prodRes.data);
      setTasks(taskRes.data);
      setTemplates(templRes.data);
      setAircraftMakes(aircraftRes.data);
    });

    if (isEdit) {
      // Reset initial load flag when loading a quote
      isInitialLoad.current = true;
      
      axios.get(`${API}/quotations/${id}`).then(res => {
        const data = res.data;
        
        // Store the totals from database - these will be displayed until user edits
        setStoredTotals({
          subtotal: data.subtotal || 0,
          total_amount: data.total_amount || 0,
          total_dealer_cost: data.total_dealer_cost || 0,
          total_profit: data.total_profit || 0,
          total_profit_margin: data.total_profit_margin || 0,
          total_hours: data.total_hours || 0,
          sales_tax_amount: data.sales_tax_amount || 0
        });
        
        // Ensure line items have profit calculated
        // Note: dealer_cost and quoted_price are stored as UNIT prices
        if (data.line_items) {
          data.line_items = data.line_items.map(item => ({
            ...item,
            profit: (item.quoted_price || 0) - (item.dealer_cost || 0),
            profit_margin: item.quoted_price > 0 
              ? (((item.quoted_price || 0) - (item.dealer_cost || 0)) / item.quoted_price * 100) 
              : 0,
            labor_cost: (item.quoted_hours || 0) * LABOR_RATE
          }));
        }
        
        // Merge loaded services with default structure to preserve flags
        if (data.additional_services) {
          data.additional_services = defaultServices.map(defaultSvc => {
            const loadedSvc = data.additional_services.find(s => s.name === defaultSvc.name);
            if (loadedSvc) {
              return {
                ...defaultSvc,
                ...loadedSvc,
                // Preserve the flags from default (stored DB docs may lack them)
                is_auto: defaultSvc.is_auto,
                auto_percentage: defaultSvc.auto_percentage,
                is_percentage: defaultSvc.is_percentage,
                percentage: defaultSvc.percentage,
                cost_rate: defaultSvc.cost_rate,
                use_fixed_price: defaultSvc.use_fixed_price,
                // Calculate profit
                profit: (loadedSvc.quoted_price || 0) - (loadedSvc.dealer_cost || 0)
              };
            }
            return defaultSvc;
          });
        }
        
        setForm(data);
        
        // Snapshot signature so we can detect user edits later
        setInitialSignature(buildSig(data));
        // Allow auto-recalc effect to run on subsequent user edits
        isInitialLoad.current = false;
        
        // Set aircraft make/model.
        // Preferred path: structured fields stored on the quote.
        if (data.aircraft_make) {
          setSelectedMake(data.aircraft_make);
          axios.get(`${API}/aircraft/models/${data.aircraft_make}`).then(res => {
            setAircraftModels(res.data);
            const model = data.aircraft_id
              ? res.data.find(m => m.id === data.aircraft_id)
              : res.data.find(m => m.model === data.aircraft_model);
            if (model) setSelectedModel(model);
          });
        } else if (data.aircraft_info) {
          // Legacy quotes only saved a free-text "aircraft_info" string
          // (e.g. "Beechcraft Bonanza 35"). Try to back-derive the dropdown
          // selections so the user doesn't have to re-pick on every edit.
          axios.get(`${API}/aircraft/makes`).then(makesRes => {
            const info = (data.aircraft_info || '').trim();
            const infoLower = info.toLowerCase();
            const makes = makesRes.data || [];
            // Find the longest matching make name that prefixes the info string
            const matchedMake = makes
              .filter(m => infoLower.startsWith(String(m).toLowerCase()))
              .sort((a, b) => b.length - a.length)[0];
            if (!matchedMake) return;
            setSelectedMake(matchedMake);
            const remainder = info.slice(matchedMake.length).trim().toLowerCase();
            axios.get(`${API}/aircraft/models/${matchedMake}`).then(modelsRes => {
              setAircraftModels(modelsRes.data);
              // Pick model whose name appears at the start of (or anywhere in) the remainder
              const model = (modelsRes.data || [])
                .filter(m => remainder.startsWith(String(m.model).toLowerCase()) || remainder.includes(String(m.model).toLowerCase()))
                .sort((a, b) => String(b.model).length - String(a.model).length)[0];
              if (model) {
                setSelectedModel(model);
                setForm(prev => ({
                  ...prev,
                  aircraft_make: matchedMake,
                  aircraft_model: model.model,
                  aircraft_id: model.id,
                }));
              } else {
                setForm(prev => ({ ...prev, aircraft_make: matchedMake }));
              }
            });
          });
        }
      });
    } else {
      // For new quotes, clear stored totals and allow auto-calc immediately
      isInitialLoad.current = false;
      setStoredTotals(null);
      // Snapshot the empty/default form so any user input flips "hasUserEdited" to true
      setInitialSignature(buildSig(form));
    }
  }, [id, isEdit]);

  // Fetch models when make changes
  useEffect(() => {
    if (selectedMake) {
      axios.get(`${API}/aircraft/models/${selectedMake}`).then(res => {
        setAircraftModels(res.data);
      });
    } else {
      setAircraftModels([]);
      setSelectedModel(null);
    }
  }, [selectedMake]);

  // Handle aircraft make selection
  const handleMakeChange = (make) => {
    setSelectedMake(make);
    setSelectedModel(null);
    setForm(prev => ({
      ...prev,
      aircraft_make: make,
      aircraft_model: '',
      aircraft_id: '',
      aircraft_info: ''
    }));
  };

  // Handle aircraft model selection
  const handleModelChange = (modelId) => {
    const model = aircraftModels.find(m => m.id === modelId);
    setSelectedModel(model);
    if (model) {
      setForm(prev => ({
        ...prev,
        aircraft_model: model.model,
        aircraft_id: model.id,
        aircraft_info: `${selectedMake} ${model.model}`
      }));
    }
  };

  // Handle saving custom aircraft
  const handleSaveCustomAircraft = async () => {
    if (!customAircraft.make.trim() || !customAircraft.model.trim()) {
      alert('Please enter both Make and Model');
      return;
    }
    
    setSavingCustomAircraft(true);
    try {
      const response = await axios.post(`${API}/aircraft`, {
        make: customAircraft.make.trim(),
        model: customAircraft.model.trim(),
        category: customAircraft.category || 'Custom',
        variants: [customAircraft.model.trim()]
      });
      
      const newAircraft = response.data;
      
      // Refresh aircraft makes list
      const makesRes = await axios.get(`${API}/aircraft/makes`);
      setAircraftMakes(makesRes.data);
      
      // Set the new aircraft as selected
      setSelectedMake(newAircraft.make);
      
      // Fetch models for the new make
      const modelsRes = await axios.get(`${API}/aircraft/models/${newAircraft.make}`);
      setAircraftModels(modelsRes.data);
      
      // Find and select the new model
      const newModel = modelsRes.data.find(m => m.id === newAircraft.id);
      setSelectedModel(newModel);
      
      // Update form
      setForm(prev => ({
        ...prev,
        aircraft_make: newAircraft.make,
        aircraft_model: newAircraft.model,
        aircraft_id: newAircraft.id,
        aircraft_info: `${newAircraft.make} ${newAircraft.model}`
      }));
      
      // Close modal and reset
      setShowCustomAircraft(false);
      setCustomAircraft({ make: '', model: '', category: 'Custom' });
      
      if (newAircraft.exists) {
        alert(`Aircraft ${newAircraft.make} ${newAircraft.model} already exists and has been selected.`);
      }
    } catch (error) {
      console.error('Error saving custom aircraft:', error);
      alert('Failed to save aircraft. Please try again.');
    } finally {
      setSavingCustomAircraft(false);
    }
  };

  const addLineItem = () => {
    setForm({
      ...form,
      line_items: [...form.line_items, {
        id: crypto.randomUUID(),
        product_id: '',
        product_name: '',
        part_number: '',
        task_id: '',
        task_description: '',
        quantity: 1,
        markup_percentage: 17.65,  // Default parts markup
        quoted_hours: 0,
        dealer_cost: 0,
        quoted_price: 0,
        labor_cost: 0,
        profit: 0,
        profit_margin: 0
      }]
    });
  };

  const removeLineItem = (index) => {
    const newItems = [...form.line_items];
    newItems.splice(index, 1);
    setForm({ ...form, line_items: newItems });
  };

  // Handle AI Assistant adding items
  const handleAIAddItems = (aiItems, customerName) => {
    const newLineItems = [...form.line_items];
    
    aiItems.forEach(item => {
      if (item.type === 'product') {
        const product = products.find(p => p.id === item.id);
        if (product) {
          const dealerCost = product.dealer_cost || 0;
          const markup = 17.65;
          const quotedPrice = dealerCost * (1 + markup / 100);
          const profit = quotedPrice - dealerCost;
          
          newLineItems.push({
            id: `ai-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            product_id: product.id,
            product_name: product.product_name,
            part_number: product.part_number,
            task_id: '',
            task_description: '',
            quantity: 1,
            markup_percentage: markup,
            quoted_hours: 0,
            dealer_cost: dealerCost,
            quoted_price: quotedPrice,
            labor_cost: 0,
            profit: profit,
            profit_margin: quotedPrice > 0 ? (profit / quotedPrice) * 100 : 0
          });
        }
      } else if (item.type === 'task') {
        const task = tasks.find(t => t.id === item.id);
        if (task) {
          const hours = task.estimated_hours || item.hours || 0;
          const quotedPrice = hours * LABOR_RATE;
          const dealerCost = hours * COST_RATE;
          const profit = quotedPrice - dealerCost;
          
          newLineItems.push({
            id: `ai-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            product_id: '',
            product_name: '',
            part_number: '',
            task_id: task.id,
            task_description: task.task_description,
            quantity: 1,
            markup_percentage: 28.5,
            quoted_hours: hours,
            dealer_cost: dealerCost,
            quoted_price: quotedPrice,
            labor_cost: hours * LABOR_RATE,
            profit: profit,
            profit_margin: quotedPrice > 0 ? (profit / quotedPrice) * 100 : 0
          });
        }
      }
    });
    
    setForm(prev => ({
      ...prev,
      line_items: newLineItems,
      party_name: customerName || prev.party_name
    }));
    
    setShowAIAssistant(false);
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...form.line_items];
    newItems[index][field] = value;

    // Auto-fill when product selected
    if (field === 'product_id' && value) {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].product_name = product.product_name;
        newItems[index].part_number = product.part_number;
        newItems[index].dealer_cost = product.dealer_cost;
        // Set default parts markup if not already set or if switching from labor
        if (newItems[index].markup_percentage === 28.5 || newItems[index].markup_percentage === 0) {
          newItems[index].markup_percentage = 17.65;
        }
        // Clear labor selection - Part and Labor are mutually exclusive
        newItems[index].task_id = '';
        newItems[index].task_description = '';
        newItems[index].quoted_hours = 0;
        newItems[index].labor_cost = 0;
      }
    }

    // Auto-fill when task selected
    if (field === 'task_id' && value) {
      const task = tasks.find(t => t.id === value);
      if (task) {
        newItems[index].task_description = task.task_description;
        newItems[index].quoted_hours = task.estimated_hours;
        // Set default labor markup
        newItems[index].markup_percentage = 28.5;
        // Clear part selection - Part and Labor are mutually exclusive
        newItems[index].product_id = '';
        newItems[index].product_name = '';
        newItems[index].part_number = '';
        // Dealer cost will be calculated from hours
      }
    }

    // Recalculate - CLEAN APPROACH:
    // - Store UNIT prices in form (dealer_cost, quoted_price are per-unit)
    // - Multiply by qty only for totals calculation
    // - This ensures backwards compatibility with all existing quotes
    const qty = newItems[index].quantity || 1;
    const markup = newItems[index].markup_percentage || 0;
    const hours = newItems[index].quoted_hours || 0;
    const dealerCost = newItems[index].dealer_cost || 0;

    // Calculate labor cost (display column)
    newItems[index].labor_cost = hours * LABOR_RATE;
    
    // Calculate UNIT prices (NOT multiplied by qty)
    if (hours > 0) {
      // Labor line item - UNIT prices
      newItems[index].quoted_price = hours * LABOR_RATE;
      newItems[index].dealer_cost = hours * COST_RATE;
    } else {
      // Parts line item - UNIT prices (dealer_cost stays as loaded from product)
      newItems[index].quoted_price = dealerCost * (1 + markup / 100);
    }
    
    // Calculate UNIT profit (per-unit)
    newItems[index].profit = newItems[index].quoted_price - newItems[index].dealer_cost;
    
    // Calculate profit margin
    newItems[index].profit_margin = newItems[index].quoted_price > 0 
      ? (newItems[index].profit / newItems[index].quoted_price * 100) 
      : 0;

    setForm({ ...form, line_items: newItems });
  };

  const updateService = (index, field, value) => {
    const newServices = [...form.additional_services];
    newServices[index][field] = value;
    
    const svc = newServices[index];
    
    // For percentage-based items (Shop Supplies, Warranty), values are auto-calculated
    if (svc.is_percentage) {
      // Just update the field, the values will be calculated in calculateTotals
      if (field === 'percentage') {
        // Allow custom percentage
      }
    }
    // For fixed price items (like IFR Certification), don't auto-calculate from hours
    else if (svc.use_fixed_price) {
      // Only recalculate profit if price or cost is manually changed
      if (field === 'quoted_price' || field === 'dealer_cost') {
        svc.profit = Math.round(((svc.quoted_price || 0) - (svc.dealer_cost || 0)) * 100) / 100;
      }
    }
    else {
      // For hours-based services, auto-calculate when hours change
      // Quoted Price = Hours × $140 (LABOR_RATE)
      // Dealer Cost = Hours × Cost Rate ($28.50 standard, $68 for Quality Checks)
      // Profit = Quoted Price - Dealer Cost
      if (field === 'quoted_hours') {
        const hours = value || 0;
        const costRate = svc.cost_rate || COST_RATE;
        svc.quoted_price = Math.round(hours * LABOR_RATE * 100) / 100;
        svc.dealer_cost = Math.round(hours * costRate * 100) / 100;
        svc.profit = Math.round((svc.quoted_price - svc.dealer_cost) * 100) / 100;
      }
      
      // Recalculate profit if price or cost is manually changed
      if (field === 'quoted_price' || field === 'dealer_cost') {
        svc.profit = Math.round(((svc.quoted_price || 0) - (svc.dealer_cost || 0)) * 100) / 100;
      }
    }
    
    setForm({ ...form, additional_services: newServices });
  };

  const loadTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setForm({
        ...form,
        line_items: template.line_items.map(item => ({ ...item, id: crypto.randomUUID() })),
        additional_services: template.additional_services.length > 0 ? template.additional_services : form.additional_services
      });
    }
  };

  const calculateTotals = () => {
    // Step 1: Calculate Line Items totals and labor hours
    // IMPORTANT: Multiply unit prices by quantity for totals
    let lineItemsQP = form.line_items.reduce((sum, item) => sum + ((item.quoted_price || 0) * (item.quantity || 1)), 0);
    let lineItemsDC = form.line_items.reduce((sum, item) => sum + ((item.dealer_cost || 0) * (item.quantity || 1)), 0);
    let lineItemsLaborHours = form.line_items.reduce((sum, item) => sum + ((item.quoted_hours || 0) * (item.quantity || 1)), 0);
    let totalHours = lineItemsLaborHours;

    // Step 2: Sum applied services (auto values are kept in sync by recalcAutoServices effect)
    let servicesQP = 0;
    let servicesDC = 0;
    form.additional_services.forEach(svc => {
      if (svc.apply) {
        servicesQP += svc.quoted_price || 0;
        servicesDC += svc.dealer_cost || 0;
        totalHours += svc.quoted_hours || 0;
      }
    });

    // Step 3: Calculate final totals (rounded consistently with backend)
    const subtotal = round2(lineItemsQP + servicesQP);
    const dealerCost = round2(lineItemsDC + servicesDC);
    const taxAmount = form.apply_tax ? round2(subtotal * (form.sales_tax_rate / 100)) : 0;
    const total = round2(subtotal + taxAmount);
    const profit = round2(subtotal - dealerCost);
    const margin = subtotal > 0 ? round2(profit / subtotal * 100) : 0;

    return { 
      subtotal, 
      dealerCost, 
      hours: round2(totalHours), 
      taxAmount, 
      total, 
      profit, 
      margin
    };
  };

  const calculatedTotals = calculateTotals();
  
  // Detect whether the user has materially edited the form.
  // Auto-computed service values are excluded from the signature, so the
  // recalc effect itself can't trigger a false-positive "dirty" state.
  const currentSignature = buildSig(form);
  const hasUserEdited = initialSignature !== null && currentSignature !== initialSignature;

  // Auto-recalculate Avionics (15% of line-item labor hours),
  // Shop Supplies (2% of subtotal-excluding-Shop-Supplies),
  // and Warranty (5% of subtotal-excluding-Warranty, when applied).
  // Order matters: Avionics first → Shop Supplies → Warranty.
  // For EXISTING quotes, this is skipped until the user actually edits something,
  // so legacy stored totals remain intact when a quote is just viewed/reopened.
  useEffect(() => {
    if (isInitialLoad.current) return;
    if (isEdit && !hasUserEdited) return; // preserve legacy stored values

    const liHours = form.line_items.reduce(
      (s, li) => s + (Number(li.quoted_hours) || 0) * (Number(li.quantity) || 1), 0
    );
    const liPrice = form.line_items.reduce(
      (s, li) => s + (Number(li.quoted_price) || 0) * (Number(li.quantity) || 1), 0
    );

    // 1. Avionics: 15% of line-item labor hours, priced at LABOR_RATE
    const avHours = round2(liHours * 0.15);
    const avPrice = round2(avHours * LABOR_RATE);
    const avDealer = round2(avHours * COST_RATE);

    let updated = form.additional_services.map(svc => {
      if (svc.name === SVC_AVIONICS && svc.is_auto) {
        return {
          ...svc,
          quoted_hours: avHours,
          quoted_price: avPrice,
          dealer_cost: avDealer,
          profit: round2(avPrice - avDealer),
        };
      }
      return svc;
    });

    // 2. Shop Supplies: 2% of (line items + all applied services except Shop Supplies & Warranty)
    //    (Warranty is itself a percentage applied AFTER Shop Supplies.)
    const baseForShop = liPrice + updated.reduce((s, svc) => {
      if (!svc.apply) return s;
      if (svc.name === SVC_SHOP_SUPPLIES) return s;
      if (svc.name === SVC_WARRANTY) return s;
      return s + (Number(svc.quoted_price) || 0);
    }, 0);
    const shopPrice = round2(baseForShop * 0.02);

    updated = updated.map(svc => {
      if (svc.name === SVC_SHOP_SUPPLIES && svc.is_auto) {
        return { ...svc, quoted_price: shopPrice, dealer_cost: 0, quoted_hours: 0, profit: shopPrice };
      }
      return svc;
    });

    // 3. Warranty: 5% of (line items + ALL other applied services including Avionics & Shop Supplies)
    const warrantyDef = updated.find(s => s.name === SVC_WARRANTY);
    if (warrantyDef && warrantyDef.is_percentage) {
      if (warrantyDef.apply) {
        const baseForWarranty = liPrice + updated.reduce((s, svc) => {
          if (!svc.apply) return s;
          if (svc.name === SVC_WARRANTY) return s;
          return s + (Number(svc.quoted_price) || 0);
        }, 0);
        const wPrice = round2(baseForWarranty * 0.05);
        updated = updated.map(svc =>
          svc.name === SVC_WARRANTY
            ? { ...svc, quoted_price: wPrice, dealer_cost: 0, quoted_hours: 0, profit: wPrice }
            : svc
        );
      } else {
        updated = updated.map(svc =>
          svc.name === SVC_WARRANTY
            ? { ...svc, quoted_price: 0, dealer_cost: 0, quoted_hours: 0, profit: 0 }
            : svc
        );
      }
    }

    // Skip setForm if nothing actually changed (prevents render loops)
    const changed = updated.some((s, i) => {
      const c = form.additional_services[i];
      if (!c) return true;
      return s.quoted_price !== c.quoted_price
        || s.quoted_hours !== c.quoted_hours
        || s.dealer_cost !== c.dealer_cost;
    });
    if (changed) {
      setForm(prev => ({ ...prev, additional_services: updated }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSignature, hasUserEdited]);

  // For unedited existing quotes, display the stored DB totals exactly (legacy preservation).
  // Once the user edits anything (line items, services, tax), switch to live calculatedTotals
  // which now reflects auto-recalculated Avionics, Shop Supplies, and Warranty.
  const totals = (isEdit && storedTotals && !hasUserEdited) ? {
    ...calculatedTotals,
    subtotal: storedTotals.subtotal,
    total: storedTotals.total_amount,
    dealerCost: storedTotals.total_dealer_cost,
    profit: storedTotals.total_profit,
    margin: storedTotals.total_profit_margin,
    hours: storedTotals.total_hours,
    taxAmount: storedTotals.sales_tax_amount
  } : calculatedTotals;
  
  // Auto-calculation of Avionics, Shop Supplies, and Warranty is handled by the
  // useEffect above. It runs only when the user actually edits the form, so
  // existing quotes preserve their stored totals until intentionally modified.

  // AI Completeness Check state
  const [completenessWarnings, setCompletenessWarnings] = useState([]);
  const [showCompletenessModal, setShowCompletenessModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const checkCompleteness = async () => {
    try {
      const response = await axios.post(`${API}/quotations/check-completeness`, form.line_items);
      return response.data;
    } catch (error) {
      console.error('Completeness check failed:', error);
      return { complete: true, warnings: [] };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check completeness before saving
    const completeness = await checkCompleteness();
    
    if (!completeness.complete && completeness.warnings.length > 0) {
      setCompletenessWarnings(completeness.warnings);
      setShowCompletenessModal(true);
      setPendingSubmit(true);
      return;
    }
    
    await saveQuotation();
  };

  const saveQuotation = async () => {
    try {
      const payload = {
        ...form,
        sales_tax_rate: form.apply_tax ? form.sales_tax_rate : 0
      };
      if (isEdit) {
        await axios.put(`${API}/quotations/${id}`, payload);
      } else {
        await axios.post(`${API}/quotations`, payload);
      }
      navigate('/quotations');
    } catch (err) {
      alert('Error saving quotation');
    }
  };

  const handleSaveAnyway = async () => {
    setShowCompletenessModal(false);
    setPendingSubmit(false);
    await saveQuotation();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-800" data-testid="quote-form-title">
          {isEdit ? 'Edit Quotation' : 'New Quotation'}
        </h1>
        <div className="flex gap-3">
          <button 
            type="button"
            onClick={() => setShowAIAssistant(true)} 
            className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md flex items-center gap-2"
            data-testid="ai-assistant-btn"
          >
            🤖 AI Assistant
          </button>
          <button type="button" onClick={() => navigate('/quotations')} className="btn-secondary">← Back</button>
        </div>
      </div>

      {/* AI Quote Assistant Modal */}
      {showAIAssistant && (
        <AIQuoteAssistant 
          onAddItems={handleAIAddItems}
          onClose={() => setShowAIAssistant(false)}
          aircraftId={form.aircraft_id}
          aircraftMake={form.aircraft_make}
          aircraftModel={form.aircraft_model}
        />
      )}

      {/* Custom Aircraft Modal */}
      {showCustomAircraft && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="custom-aircraft-modal">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Add Custom Aircraft</h3>
              <button 
                onClick={() => setShowCustomAircraft(false)}
                className="text-slate-400 hover:text-slate-600"
                data-testid="close-custom-aircraft-modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Add a new aircraft that's not in our database. It will be saved for future quotes.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Make / Manufacturer <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={customAircraft.make}
                  onChange={(e) => setCustomAircraft(prev => ({ ...prev, make: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  placeholder="e.g., Cirrus, Robinson, Grob"
                  data-testid="custom-aircraft-make"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Model <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={customAircraft.model}
                  onChange={(e) => setCustomAircraft(prev => ({ ...prev, model: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  placeholder="e.g., SR22T, R44, G120TP"
                  data-testid="custom-aircraft-model"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <select
                  value={customAircraft.category}
                  onChange={(e) => setCustomAircraft(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  data-testid="custom-aircraft-category"
                >
                  <option value="Custom">Custom</option>
                  <option value="Single Engine">Single Engine</option>
                  <option value="Multi Engine">Multi Engine</option>
                  <option value="Turboprop">Turboprop</option>
                  <option value="Jet">Jet</option>
                  <option value="Helicopter">Helicopter</option>
                  <option value="Experimental">Experimental</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowCustomAircraft(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
                data-testid="cancel-custom-aircraft"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCustomAircraft}
                disabled={savingCustomAircraft || !customAircraft.make.trim() || !customAircraft.model.trim()}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="save-custom-aircraft"
              >
                {savingCustomAircraft ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save & Select'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completeness Warning Modal */}
      {showCompletenessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="completeness-modal">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Quote May Be Incomplete</h3>
                <p className="text-sm text-slate-500">We found some potential issues</p>
              </div>
            </div>
            
            <div className="space-y-3 mb-6">
              {completenessWarnings.map((warning, idx) => (
                <div key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800 font-medium">{warning.message}</p>
                  {warning.missing && warning.missing.length > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Consider adding: {warning.missing.join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => { setShowCompletenessModal(false); setPendingSubmit(false); }}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                data-testid="completeness-go-back"
              >
                Go Back & Add Items
              </button>
              <button
                type="button"
                onClick={handleSaveAnyway}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                data-testid="completeness-save-anyway"
              >
                Save Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Aircraft Selection - REQUIRED */}
        <div className="bg-gradient-to-r from-sky-50 to-indigo-50 rounded-xl shadow-lg p-6 border-2 border-sky-200">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">✈️</span>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Select Aircraft</h2>
              <p className="text-sm text-slate-500">Required - This helps us find the right products for your quote</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Aircraft Make <span className="text-red-500">*</span></label>
              <select
                value={selectedMake}
                onChange={(e) => handleMakeChange(e.target.value)}
                className="form-select w-full"
                required
                data-testid="aircraft-make"
              >
                <option value="">Select Make...</option>
                {aircraftMakes.map(make => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Model <span className="text-red-500">*</span></label>
              <select
                value={selectedModel?.id || ''}
                onChange={(e) => handleModelChange(e.target.value)}
                className="form-select w-full"
                required
                disabled={!selectedMake}
                data-testid="aircraft-model"
              >
                <option value="">{selectedMake ? 'Select Model...' : 'Select make first'}</option>
                {aircraftModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.model} ({model.category})
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Add Custom Aircraft Button */}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowCustomAircraft(true)}
              className="text-sm text-sky-600 hover:text-sky-800 flex items-center gap-1"
              data-testid="add-custom-aircraft-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Aircraft not in list? Add custom aircraft
            </button>
          </div>
          
          {selectedModel && (
            <div className="mt-4 p-3 bg-white rounded-lg border border-sky-200">
              <p className="text-sm text-slate-600">
                <span className="font-medium text-sky-700">Selected:</span> {selectedMake} {selectedModel.model}
                <span className="text-slate-400 ml-2">• Variants: {selectedModel.variants?.slice(0, 5).join(', ')}</span>
              </p>
            </div>
          )}
        </div>

        {/* Customer Info */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Customer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="Customer Name" value={form.party_name} onChange={e => setForm({...form, party_name: e.target.value})} required data-testid="customer-name" />
            <Input label="Email" type="email" value={form.party_email} onChange={e => setForm({...form, party_email: e.target.value})} data-testid="customer-email" />
            <Input label="Tail Number / Notes" value={form.aircraft_info} onChange={e => setForm({...form, aircraft_info: e.target.value})} placeholder="e.g., N12345" data-testid="aircraft-info" />
          </div>
        </div>

        {/* Template Selection */}
        {templates.length > 0 && (
          <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-4">
            <span className="text-blue-700 font-medium">Load from Template:</span>
            <select onChange={e => loadTemplate(e.target.value)} className="form-select rounded-lg border-blue-200" data-testid="template-select">
              <option value="">Select a template...</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.template_name}</option>)}
            </select>
          </div>
        )}

        {/* Line Items */}
        <div className="bg-white rounded-xl shadow-lg p-6" style={{ position: 'relative', zIndex: 10 }}>
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Parts & Labor</h2>

          <div style={{ overflow: 'visible' }}>
            <table className="w-full text-sm" data-testid="line-items-table" style={{ overflow: 'visible' }}>
              <thead className="bg-sky-100">
                <tr>
                  <th className="px-2 py-3 text-center w-10"></th>
                  <th className="px-2 py-3 text-center w-8">#</th>
                  <th className="px-2 py-3 text-left" style={{ minWidth: '200px' }}>Part*</th>
                  <th className="px-2 py-3 text-left" style={{ minWidth: '200px' }}>Labor*</th>
                  <th className="px-2 py-3 text-center" style={{ minWidth: '90px' }}>Markup %</th>
                  <th className="px-2 py-3 text-center" style={{ minWidth: '70px' }}>Qty</th>
                  <th className="px-2 py-3 text-right" style={{ minWidth: '100px' }}>Quoted Price</th>
                  <th className="px-2 py-3 text-right" style={{ minWidth: '90px' }}>Labor</th>
                  <th className="px-2 py-3 text-right" style={{ minWidth: '100px' }}>Dealer Cost</th>
                  <th className="px-2 py-3 text-right" style={{ minWidth: '90px' }}>Profit</th>
                  <th className="px-2 py-3 text-center" style={{ minWidth: '80px' }}>Margin %</th>
                  <th className="px-2 py-3 text-center" style={{ minWidth: '80px' }}>Hours</th>
                  <th className="px-2 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {form.line_items.map((item, index) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-2 py-2">
                      <button type="button" onClick={() => removeLineItem(index)} className="bg-red-500 hover:bg-red-600 text-white w-6 h-6 rounded text-xs font-bold">✕</button>
                    </td>
                    <td className="px-2 py-2 text-center font-medium">{index + 1}</td>
                    <td className="px-2 py-2" style={{ minWidth: '200px' }}>
                      <SearchableSelect
                        value={item.product_id || ''}
                        onChange={(val) => updateLineItem(index, 'product_id', val)}
                        options={products
                          .map(p => ({ 
                            id: p.id, 
                            display: p.product_name 
                              ? `${p.product_name} (${p.part_number})` 
                              : p.part_number || 'Unknown Product',
                            product_name: p.product_name || '',
                            part_number: p.part_number || ''
                          }))
                          .sort((a, b) => {
                            if (a.product_name && !b.product_name) return -1;
                            if (!a.product_name && b.product_name) return 1;
                            if (a.product_name && b.product_name) {
                              return a.product_name.localeCompare(b.product_name);
                            }
                            return a.part_number.localeCompare(b.part_number);
                          })}
                        placeholder="Select Part..."
                        displayKey="display"
                        valueKey="id"
                        searchKeys={['product_name', 'part_number']}
                      />
                    </td>
                    <td className="px-2 py-2" style={{ minWidth: '200px' }}>
                      <SearchableSelect
                        value={item.task_id || ''}
                        onChange={(val) => updateLineItem(index, 'task_id', val)}
                        options={tasks
                          .map(t => ({ 
                            id: t.id, 
                            task_description: t.task_description 
                          }))
                          .sort((a, b) => a.task_description.localeCompare(b.task_description))}
                        placeholder="Select Labor..."
                        displayKey="task_description"
                        valueKey="id"
                        searchKeys={['task_description']}
                      />
                    </td>
                    <td className="px-2 py-2" style={{ minWidth: '90px' }}>
                      <input type="number" value={item.markup_percentage} onChange={e => updateLineItem(index, 'markup_percentage', parseFloat(e.target.value) || 0)} className="form-input text-sm w-full text-center font-medium" step="0.01" data-testid={`line-item-markup-${index}`} />
                    </td>
                    <td className="px-2 py-2" style={{ minWidth: '70px' }}>
                      <input type="number" value={item.quantity} onChange={e => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 1)} className="form-input text-sm w-full text-center font-medium" min="1" data-testid={`line-item-qty-${index}`} />
                    </td>
                    <td className="px-2 py-2 text-right font-mono font-semibold text-slate-800" style={{ minWidth: '100px' }}>{(item.quoted_price * (item.quantity || 1)).toFixed(2)}</td>
                    <td className="px-2 py-2 text-right font-mono text-slate-600" style={{ minWidth: '90px' }}>{(item.labor_cost * (item.quantity || 1)).toFixed(2)}</td>
                    <td className="px-2 py-2 text-right font-mono text-slate-600" style={{ minWidth: '100px' }}>{(item.dealer_cost * (item.quantity || 1)).toFixed(2)}</td>
                    <td className="px-2 py-2 text-right font-mono font-semibold text-green-600" style={{ minWidth: '90px' }}>{(item.profit * (item.quantity || 1)).toFixed(2)}</td>
                    <td className="px-2 py-2 text-center font-mono text-blue-600" style={{ minWidth: '80px' }}>{item.profit_margin.toFixed(2)}</td>
                    <td className="px-2 py-2" style={{ minWidth: '80px' }}>
                      <input type="number" step="0.5" value={item.quoted_hours} onChange={e => updateLineItem(index, 'quoted_hours', parseFloat(e.target.value) || 0)} className="form-input text-sm w-full text-center font-medium" data-testid={`line-item-hours-${index}`} />
                    </td>
                    <td className="px-2 py-2">
                      {index === form.line_items.length - 1 && (
                        <button type="button" onClick={addLineItem} className="bg-green-500 hover:bg-green-600 text-white w-6 h-6 rounded text-xs font-bold" data-testid="add-line-item">+</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {form.line_items.length === 0 && (
            <div className="text-center py-4">
              <button type="button" onClick={addLineItem} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded font-medium" data-testid="add-first-item">+ Add First Item</button>
            </div>
          )}
        </div>

        {/* Additional Services */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Additional Services</h2>
          <div className="overflow-visible">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  <th className="px-3 py-2 text-left w-10">✓</th>
                  <th className="px-3 py-2 text-left">Service</th>
                  <th className="px-3 py-2 text-right" style={{ minWidth: '110px' }}>Quoted Price</th>
                  <th className="px-3 py-2 text-right" style={{ minWidth: '110px' }}>Dealer Cost</th>
                  <th className="px-3 py-2 text-right" style={{ minWidth: '100px' }}>Profit</th>
                  <th className="px-3 py-2 text-right" style={{ minWidth: '90px' }}>Margin %</th>
                  <th className="px-3 py-2 text-center" style={{ minWidth: '90px' }}>Hours</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {form.additional_services.map((svc, index) => {
                  const margin = svc.quoted_price > 0 ? ((svc.profit / svc.quoted_price) * 100) : 0;
                  const isPercentage = svc.is_percentage;
                  const isAuto = svc.is_auto;
                  return (
                    <tr key={svc.name} className={svc.apply ? 'bg-cyan-50' : 'hover:bg-slate-50'}>
                      <td className="px-3 py-3">
                        {isAuto ? (
                          <span className="text-cyan-600 font-bold text-base" title="Always enabled">✓</span>
                        ) : (
                          <input 
                            type="checkbox" 
                            checked={svc.apply} 
                            onChange={e => updateService(index, 'apply', e.target.checked)}
                            className="w-5 h-5 text-cyan-600 rounded cursor-pointer"
                            data-testid={`service-checkbox-${index}`}
                          />
                        )}
                      </td>
                      <td className="px-3 py-3 font-medium text-slate-700">
                        {svc.name}
                        {isAuto && !isPercentage && <span className="text-xs text-cyan-600 ml-2 bg-cyan-100 px-2 py-0.5 rounded">(15% of labor hrs)</span>}
                        {isPercentage && <span className="text-xs text-slate-500 ml-2 bg-slate-100 px-2 py-0.5 rounded">({svc.percentage}%)</span>}
                      </td>
                      <td className="px-3 py-3 text-right font-mono" style={{ minWidth: '110px' }}>
                        {(isPercentage || isAuto) ? (
                          <span className={`font-semibold ${svc.apply ? 'text-slate-800' : 'text-slate-400'}`}>{(svc.quoted_price || 0).toFixed(2)}</span>
                        ) : (
                          <input 
                            type="number" 
                            step="0.01" 
                            value={svc.quoted_price} 
                            onChange={e => updateService(index, 'quoted_price', parseFloat(e.target.value) || 0)} 
                            className="form-input text-sm w-full text-right font-mono font-medium"
                            disabled={!svc.apply}
                            style={{ minWidth: '90px' }}
                          />
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-mono" style={{ minWidth: '110px' }}>
                        {isPercentage ? (
                          <span className="text-slate-400">0.00</span>
                        ) : isAuto ? (
                          <span className="font-semibold text-slate-800">{(svc.dealer_cost || 0).toFixed(2)}</span>
                        ) : (
                          <input 
                            type="number" 
                            step="0.01" 
                            value={svc.dealer_cost} 
                            onChange={e => updateService(index, 'dealer_cost', parseFloat(e.target.value) || 0)} 
                            className="form-input text-sm w-full text-right font-mono font-medium"
                            disabled={!svc.apply}
                            style={{ minWidth: '90px' }}
                          />
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-mono font-semibold text-green-600" style={{ minWidth: '100px' }}>
                        ${(svc.profit || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-blue-600" style={{ minWidth: '90px' }}>
                        {isPercentage ? '100.00' : margin.toFixed(2)}%
                      </td>
                      <td className="px-3 py-3 text-center" style={{ minWidth: '90px' }}>
                        {isPercentage ? (
                          <span className="text-slate-400">-</span>
                        ) : isAuto ? (
                          <span className="font-semibold text-slate-800">{(svc.quoted_hours || 0).toFixed(2)}</span>
                        ) : (
                          <input 
                            type="number" 
                            step="0.25" 
                            value={svc.quoted_hours} 
                            onChange={e => updateService(index, 'quoted_hours', parseFloat(e.target.value) || 0)} 
                            className="form-input text-sm w-full text-center font-medium"
                            disabled={!svc.apply || svc.use_fixed_price}
                            style={{ minWidth: '70px' }}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Sales Tax */}
          <div className="mt-4 flex items-center gap-4 p-4 bg-slate-50 rounded-lg" data-testid="sales-tax-section">
            <input 
              type="checkbox" 
              checked={form.apply_tax} 
              onChange={e => setForm({...form, apply_tax: e.target.checked})}
              className="w-5 h-5 rounded border-slate-300"
              data-testid="apply-tax-checkbox"
            />
            <label className="font-medium text-slate-700">TN Sales Tax</label>
            {form.apply_tax && (
              <>
                <input 
                  type="number" 
                  step="0.01" 
                  value={form.sales_tax_rate} 
                  onChange={e => setForm({...form, sales_tax_rate: parseFloat(e.target.value) || 0})}
                  className="form-input text-sm w-20 text-center"
                  data-testid="tax-rate-input"
                />
                <span className="text-slate-500">%</span>
                <span className="text-slate-700 font-medium ml-2">Tax Amount: ${totals.taxAmount.toFixed(2)}</span>
              </>
            )}
          </div>
        </div>

        {/* Totals */}
        <div className="bg-slate-800 text-white rounded-xl p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-slate-400 text-sm">Subtotal</p>
              <p className="text-2xl font-bold">${totals.subtotal.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Dealer Cost</p>
              <p className="text-xl font-semibold text-slate-300">${totals.dealerCost.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Profit ({totals.margin.toFixed(1)}%)</p>
              <p className="text-xl font-semibold text-green-400">${totals.profit.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Total Hours</p>
              <p className="text-xl font-semibold">{totals.hours.toFixed(1)} hrs</p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-700 flex items-center gap-4">
            <div className="flex-1 flex items-center gap-3">
              <input 
                type="checkbox" 
                checked={form.apply_tax} 
                onChange={e => setForm({...form, apply_tax: e.target.checked})}
                className="w-4 h-4 rounded border-slate-500"
              />
              <label className="text-slate-400 text-sm">TN Sales Tax</label>
              {form.apply_tax && (
                <input type="number" step="0.01" value={form.sales_tax_rate} onChange={e => setForm({...form, sales_tax_rate: parseFloat(e.target.value) || 0})} className="form-input w-20 ml-1 text-slate-800 text-sm" />
              )}
            </div>
            <div>
              <p className="text-slate-400 text-sm">Tax Amount</p>
              <p className="text-xl font-semibold">${totals.taxAmount.toFixed(2)}</p>
            </div>
            <div className="text-right">
              <p className="text-slate-400 text-sm">TOTAL</p>
              <p className="text-3xl font-bold text-cyan-400">${totals.total.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Notes & Submit */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <Input label="Notes / Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} textarea rows={3} />
          <div className="mt-6 flex justify-end gap-4">
            <button type="button" onClick={() => navigate('/quotations')} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary text-lg px-8" data-testid="save-quote-btn">
              {isEdit ? 'Update Quotation' : 'Create Quotation'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// ===================
// TEMPLATES
// ===================
const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/templates`).then(res => setTemplates(res.data));
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Delete this template?')) {
      await axios.delete(`${API}/templates/${id}`);
      setTemplates(templates.filter(t => t.id !== id));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-800" data-testid="templates-title">Quote Templates</h1>
        <button onClick={() => navigate('/templates/new')} className="btn-primary" data-testid="new-template-btn">
          + New Template
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map(template => (
          <div key={template.id} className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{template.template_name}</h3>
            <p className="text-slate-500 text-sm mb-4">{template.description || 'No description'}</p>
            <p className="text-sm text-slate-600 mb-4">{template.line_items?.length || 0} items</p>
            <div className="flex gap-2">
              <button onClick={() => handleDelete(template.id)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500">
            No templates yet. Create one to speed up quote creation!
          </div>
        )}
      </div>
    </div>
  );
};

// ===================
// SHARED COMPONENTS
// ===================
const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
    <div className="rounded-xl shadow-2xl w-full max-w-lg mx-4" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
      <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h2>
        <button onClick={onClose} className="text-2xl transition-colors" style={{ color: 'var(--text-muted)' }}>×</button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

// Searchable Select Component
const SearchableSelect = ({ value, onChange, options, placeholder, displayKey, valueKey, searchKeys }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedOption = options.find(opt => opt[valueKey] === value);
  
  const filteredOptions = options.filter(opt => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return searchKeys.some(key => 
      opt[key]?.toString().toLowerCase().includes(searchLower)
    );
  });

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearch('');
  };

  // Truncate display text for button
  const displayText = selectedOption ? selectedOption[displayKey] : placeholder;
  const truncatedText = displayText && displayText.length > 30 ? displayText.substring(0, 30) + '...' : displayText;

  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="form-select text-sm w-full text-left bg-white border border-slate-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 pr-6 overflow-hidden"
        data-testid="searchable-select-trigger"
        title={displayText}
      >
        <span className={`block truncate ${selectedOption ? 'text-slate-800' : 'text-slate-400'}`}>
          {truncatedText}
        </span>
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">▼</span>
      </button>
      
      {isOpen && (
        <>
          {/* Backdrop to close on click outside */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => { setIsOpen(false); setSearch(''); }}
          />
          
          {/* Dropdown */}
          <div className="absolute mt-1 bg-white border border-slate-200 rounded-lg shadow-xl left-0 min-w-[350px] max-w-[500px]" style={{ zIndex: 9999 }} data-testid="searchable-select-dropdown">
            <div className="p-2 border-b">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to search..."
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                autoFocus
                onClick={(e) => e.stopPropagation()}
                data-testid="searchable-select-input"
              />
            </div>
            <div className="max-h-60 overflow-y-auto">
              <button
                type="button"
                onClick={() => handleSelect('')}
                className="w-full text-left px-3 py-2 text-sm text-slate-400 hover:bg-slate-100"
              >
                {placeholder}
              </button>
              {filteredOptions.length > 0 ? (
                filteredOptions.slice(0, 100).map(opt => (
                  <button
                    type="button"
                    key={opt[valueKey]}
                    onClick={() => handleSelect(opt[valueKey])}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 truncate ${
                      opt[valueKey] === value ? 'bg-cyan-100 text-cyan-800' : 'text-slate-700'
                    }`}
                    title={opt[displayKey]}
                  >
                    {opt[displayKey]}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-sm text-slate-500 text-center">No results found</div>
              )}
              {filteredOptions.length > 100 && (
                <div className="px-3 py-2 text-xs text-slate-400 text-center border-t">
                  Showing 100 of {filteredOptions.length} results. Type to narrow down.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Input = ({ label, textarea, size, ...props }) => (
  <div className={size === 'sm' ? '' : 'space-y-1'}>
    {label && <label className={`block font-medium text-slate-700 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>{label}</label>}
    {textarea ? (
      <textarea {...props} className={`form-textarea w-full rounded-lg border-slate-300 ${props.className || ''}`} />
    ) : (
      <input {...props} className={`form-input w-full rounded-lg border-slate-300 ${size === 'sm' ? 'text-sm py-1' : ''} ${props.className || ''}`} />
    )}
  </div>
);

// ===================
// PRICE IMPORT PAGE
// ===================
const PriceImport = () => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/import/prices`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data);
      if (!response.data.success) {
        setError(response.data.errors?.join(', ') || 'Import failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="price-import-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Price List Import</h1>
          <p className="text-slate-500 mt-1">Upload a Garmin price list CSV to update product prices</p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Upload Price List</h2>
        
        <div className="space-y-4">
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-cyan-500 transition-colors">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="price-file-input"
              data-testid="price-file-input"
            />
            <label htmlFor="price-file-input" className="cursor-pointer">
              <div className="text-4xl mb-2">📄</div>
              <p className="text-slate-600 font-medium">
                {file ? file.name : 'Click to select a CSV file'}
              </p>
              <p className="text-slate-400 text-sm mt-1">
                Expected format: ItemNo/Part Number, Product Name, List Price, Dealer Price
              </p>
            </label>
          </div>

          {file && (
            <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📋</span>
                <div>
                  <p className="font-medium text-slate-700">{file.name}</p>
                  <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <button
                onClick={() => { setFile(null); setResult(null); }}
                className="text-slate-400 hover:text-red-500"
              >
                ✕
              </button>
            </div>
          )}

          <button
            onClick={handleImport}
            disabled={!file || importing}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="import-button"
          >
            {importing ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Importing...
              </span>
            ) : (
              '📥 Import Prices'
            )}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="import-error">
          <div className="flex items-start gap-3">
            <span className="text-2xl">❌</span>
            <div>
              <h3 className="font-semibold text-red-800">Import Error</h3>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {result && result.success && (
        <div className="space-y-4" data-testid="import-results">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="text-3xl mb-2">📊</div>
              <p className="text-2xl font-bold text-slate-800">{result.total_in_csv}</p>
              <p className="text-sm text-slate-500">Products in CSV</p>
            </div>
            <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-4">
              <div className="text-3xl mb-2">✅</div>
              <p className="text-2xl font-bold text-green-700">{result.products_updated}</p>
              <p className="text-sm text-green-600">Prices Updated</p>
            </div>
            <div className="bg-blue-50 rounded-xl shadow-sm border border-blue-200 p-4">
              <div className="text-3xl mb-2">➕</div>
              <p className="text-2xl font-bold text-blue-700">{result.products_added}</p>
              <p className="text-sm text-blue-600">New Products Added</p>
            </div>
            <div className="bg-amber-50 rounded-xl shadow-sm border border-amber-200 p-4">
              <div className="text-3xl mb-2">⚠️</div>
              <p className="text-2xl font-bold text-amber-700">{result.products_not_found}</p>
              <p className="text-sm text-amber-600">Not in CSV (Garmin)</p>
            </div>
          </div>

          {/* Price Updates Table */}
          {result.price_updates && result.price_updates.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800">Price Updates (Top {result.price_updates.length})</h3>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600">Part Number</th>
                      <th className="text-left p-3 font-medium text-slate-600">Product</th>
                      <th className="text-right p-3 font-medium text-slate-600">Old Price</th>
                      <th className="text-right p-3 font-medium text-slate-600">New Price</th>
                      <th className="text-right p-3 font-medium text-slate-600">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.price_updates.map((item, idx) => (
                      <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-3 font-mono text-xs">{item.part_number}</td>
                        <td className="p-3">{item.name}</td>
                        <td className="p-3 text-right">${item.old_price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                        <td className="p-3 text-right font-medium">${item.new_price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                        <td className={`p-3 text-right font-medium ${item.difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {item.difference > 0 ? '↑' : '↓'} ${Math.abs(item.difference)?.toLocaleString(undefined, {minimumFractionDigits: 2})} ({item.percent_change}%)
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* New Products Table */}
          {result.new_products && result.new_products.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800">New Products Added ({result.new_products.length})</h3>
              </div>
              <div className="overflow-x-auto max-h-64">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600">Part Number</th>
                      <th className="text-left p-3 font-medium text-slate-600">Product</th>
                      <th className="text-right p-3 font-medium text-slate-600">List Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.new_products.map((item, idx) => (
                      <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-3 font-mono text-xs">{item.part_number}</td>
                        <td className="p-3">{item.name}</td>
                        <td className="p-3 text-right font-medium">${item.list_price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Missing from CSV (Info) */}
          {result.missing_from_db && result.missing_from_db.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800">Garmin Products Not in CSV ({result.products_not_found} total, showing {result.missing_from_db.length})</h3>
                <p className="text-sm text-slate-500 mt-1">These products exist in your database but were not found in the uploaded price list</p>
              </div>
              <div className="overflow-x-auto max-h-64">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600">Part Number</th>
                      <th className="text-left p-3 font-medium text-slate-600">Product</th>
                      <th className="text-right p-3 font-medium text-slate-600">Current Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.missing_from_db.map((item, idx) => (
                      <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-3 font-mono text-xs">{item.part_number}</td>
                        <td className="p-3">{item.name}</td>
                        <td className="p-3 text-right">${item.list_price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-slate-50 rounded-xl p-6">
        <h3 className="font-semibold text-slate-800 mb-3">📋 CSV Format Requirements</h3>
        <ul className="space-y-2 text-sm text-slate-600">
          <li>• File must be in CSV format (.csv)</li>
          <li>• Required columns: <code className="bg-slate-200 px-1 rounded">ItemNo/Part Number</code>, <code className="bg-slate-200 px-1 rounded">Product Name</code>, <code className="bg-slate-200 px-1 rounded">List Price</code>, <code className="bg-slate-200 px-1 rounded">Dealer Price</code></li>
          <li>• First row should contain column headers</li>
          <li>• Prices can include commas (e.g., "1,234.56")</li>
          <li>• Products are matched by Part Number</li>
        </ul>
      </div>
    </div>
  );
};

// ===================
// AUDIT LOGS PAGE
// ===================
const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ entity_type: '', action: '' });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.entity_type) params.append('entity_type', filter.entity_type);
      if (filter.action) params.append('action', filter.action);
      params.append('limit', '200');
      
      const response = await axios.get(`${API}/audit-logs?${params}`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'bg-green-100 text-green-800';
      case 'UPDATE': return 'bg-blue-100 text-blue-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      case 'LOGIN': return 'bg-purple-100 text-purple-800';
      case 'LOGOUT': return 'bg-slate-100 text-slate-800';
      case 'BACKUP': return 'bg-amber-100 text-amber-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Audit Logs</h1>
          <p className="text-slate-500 mt-1">Track all changes and user activities</p>
        </div>
        <button onClick={fetchLogs} className="btn-secondary" data-testid="refresh-logs">
          🔄 Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Entity Type</label>
            <select
              value={filter.entity_type}
              onChange={(e) => setFilter(prev => ({ ...prev, entity_type: e.target.value }))}
              className="form-select rounded-lg border-slate-300"
            >
              <option value="">All Types</option>
              <option value="user">User</option>
              <option value="product">Product</option>
              <option value="task">Task</option>
              <option value="quotation">Quotation</option>
              <option value="template">Template</option>
              <option value="system">System</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Action</label>
            <select
              value={filter.action}
              onChange={(e) => setFilter(prev => ({ ...prev, action: e.target.value }))}
              className="form-select rounded-lg border-slate-300"
            >
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
              <option value="BACKUP">Backup</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto"></div>
            <p className="mt-2 text-slate-500">Loading logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No audit logs found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Timestamp</th>
                  <th className="text-left p-3 font-medium text-slate-600">User</th>
                  <th className="text-left p-3 font-medium text-slate-600">Action</th>
                  <th className="text-left p-3 font-medium text-slate-600">Entity</th>
                  <th className="text-left p-3 font-medium text-slate-600">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3 text-slate-600 whitespace-nowrap">{formatDate(log.timestamp)}</td>
                    <td className="p-3 font-medium">{log.username}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-slate-500">{log.entity_type}</span>
                      {log.entity_name && (
                        <span className="block text-slate-700 font-medium">{log.entity_name}</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-500 max-w-xs truncate">
                      {Object.keys(log.changes || {}).length > 0 && (
                        <span title={JSON.stringify(log.changes, null, 2)}>
                          {Object.keys(log.changes).join(', ')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ===================
// BACKUPS PAGE
// ===================
const BackupsPage = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backing, setBacking] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/backups`);
      setBackups(response.data);
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackups();
  }, []);

  const triggerBackup = async () => {
    setBacking(true);
    setMessage(null);
    try {
      const response = await axios.post(`${API}/backup`);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Backup completed successfully!' });
        fetchBackups();
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Backup failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Backup failed' });
    } finally {
      setBacking(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="backups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Database Backups</h1>
          <p className="text-slate-500 mt-1">Automatic daily backups at 2 AM • Last 7 backups retained</p>
        </div>
        <button
          onClick={triggerBackup}
          disabled={backing}
          className="btn-primary disabled:opacity-50"
          data-testid="trigger-backup"
        >
          {backing ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Backing up...
            </span>
          ) : (
            '💾 Backup Now'
          )}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* Backups List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto"></div>
            <p className="mt-2 text-slate-500">Loading backups...</p>
          </div>
        ) : backups.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-2">💾</div>
            <p className="text-slate-600 font-medium">No backups yet</p>
            <p className="text-slate-500 text-sm mt-1">Click "Backup Now" to create your first backup</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {backups.map((backup, index) => (
              <div key={backup.timestamp} className="p-4 hover:bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${index === 0 ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                    💾
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">
                      Backup {backup.timestamp}
                      {index === 0 && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Latest</span>}
                    </p>
                    <p className="text-sm text-slate-500">
                      {backup.collections_backed_up?.length || 0} collections • {backup.size_mb?.toFixed(2) || '0'} MB
                    </p>
                  </div>
                </div>
                <div className="text-sm text-slate-500">
                  {new Date(backup.timestamp.replace(/_/g, ' ').replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-slate-50 rounded-xl p-6">
        <h3 className="font-semibold text-slate-800 mb-3">📋 Backup Information</h3>
        <ul className="space-y-2 text-sm text-slate-600">
          <li>• Backups run automatically every day at 2:00 AM</li>
          <li>• Each backup includes: products, tasks, quotations, templates, aircraft, users, audit logs, customers</li>
          <li>• Maximum 7 backups are retained (older backups are automatically deleted)</li>
          <li>• Backups are stored locally on the server</li>
        </ul>
      </div>
    </div>
  );
};

// ===================
// CUSTOMERS PAGE
// ===================
const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '',
    address: '', city: '', state: '', zip_code: '', notes: '',
    aircraft: []
  });

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, form);
      } else {
        await axios.post(`${API}/customers`, form);
      }
      fetchCustomers();
      resetForm();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error saving customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setForm({
      name: customer.name || '',
      company: customer.company || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip_code: customer.zip_code || '',
      notes: customer.notes || '',
      aircraft: customer.aircraft || []
    });
    setShowForm(true);
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete customer "${customer.name}"?`)) return;
    try {
      await axios.delete(`${API}/customers/${customer.id}`);
      fetchCustomers();
    } catch (error) {
      alert('Error deleting customer');
    }
  };

  const resetForm = () => {
    setForm({
      name: '', company: '', email: '', phone: '',
      address: '', city: '', state: '', zip_code: '', notes: '',
      aircraft: []
    });
    setEditingCustomer(null);
    setShowForm(false);
  };

  const addAircraft = () => {
    setForm(prev => ({
      ...prev,
      aircraft: [...prev.aircraft, { make: '', model: '', tail_number: '' }]
    }));
  };

  const updateAircraft = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      aircraft: prev.aircraft.map((ac, i) => i === index ? { ...ac, [field]: value } : ac)
    }));
  };

  const removeAircraft = (index) => {
    setForm(prev => ({
      ...prev,
      aircraft: prev.aircraft.filter((_, i) => i !== index)
    }));
  };

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Customers</h1>
          <p className="text-slate-500 mt-1">Manage customer information and aircraft</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="add-customer-btn">
          + Add Customer
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <input
          type="text"
          placeholder="Search customers by name, company, or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg"
          data-testid="customer-search"
        />
      </div>

      {/* Customer Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h2>
              <button onClick={resetForm} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg" required data-testid="customer-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
                  <input type="text" value={form.company} onChange={(e) => setForm(prev => ({ ...prev, company: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="customer-company" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="customer-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input type="tel" value={form.phone} onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="customer-phone" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <input type="text" value={form.address} onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                  <input type="text" value={form.city} onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                  <input type="text" value={form.state} onChange={(e) => setForm(prev => ({ ...prev, state: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">ZIP</label>
                  <input type="text" value={form.zip_code} onChange={(e) => setForm(prev => ({ ...prev, zip_code: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
              </div>
              
              {/* Aircraft Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">Aircraft</label>
                  <button type="button" onClick={addAircraft} className="text-sm text-cyan-600 hover:text-cyan-800">
                    + Add Aircraft
                  </button>
                </div>
                {form.aircraft.map((ac, idx) => (
                  <div key={idx} className="flex gap-2 mb-2 items-center">
                    <input type="text" placeholder="Make" value={ac.make} onChange={(e) => updateAircraft(idx, 'make', e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                    <input type="text" placeholder="Model" value={ac.model} onChange={(e) => updateAircraft(idx, 'model', e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                    <input type="text" placeholder="Tail #" value={ac.tail_number} onChange={(e) => updateAircraft(idx, 'tail_number', e.target.value)}
                      className="w-24 px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                    <button type="button" onClick={() => removeAircraft(idx)} className="text-red-500 hover:text-red-700">✕</button>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg" rows={2} />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editingCustomer ? 'Update' : 'Create'} Customer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Customers List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto"></div>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            {searchTerm ? 'No customers found matching your search' : 'No customers yet. Add your first customer!'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Name</th>
                  <th className="text-left p-3 font-medium text-slate-600">Company</th>
                  <th className="text-left p-3 font-medium text-slate-600">Contact</th>
                  <th className="text-left p-3 font-medium text-slate-600">Aircraft</th>
                  <th className="text-right p-3 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-800">{customer.name}</td>
                    <td className="p-3 text-slate-600">{customer.company || '-'}</td>
                    <td className="p-3">
                      <div className="text-slate-600">{customer.email || '-'}</div>
                      <div className="text-slate-400 text-xs">{customer.phone}</div>
                    </td>
                    <td className="p-3">
                      {customer.aircraft?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {customer.aircraft.map((ac, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded text-xs">
                              {ac.tail_number || `${ac.make} ${ac.model}`}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => handleEdit(customer)} className="text-cyan-600 hover:text-cyan-800 mr-2">Edit</button>
                      <button onClick={() => handleDelete(customer)} className="text-red-600 hover:text-red-800">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ===================
// APP
// ===================
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <Routes>
              {/* Public route - Login */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Protected routes */}
              <Route path="/*" element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/products" element={<Products />} />
                      <Route path="/tasks" element={<Tasks />} />
                      <Route path="/quotations" element={<Quotations />} />
                      <Route path="/quotations/new" element={<QuotationForm />} />
                      <Route path="/quotations/:id" element={<QuotationForm />} />
                      <Route path="/quotations/:id/edit" element={<QuotationForm />} />
                      <Route path="/templates" element={<Templates />} />
                      <Route path="/customers" element={<CustomersPage />} />
                      <Route path="/import-prices" element={<PriceImport />} />
                      <Route path="/audit-logs" element={<AuditLogs />} />
                      <Route path="/backups" element={<BackupsPage />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              } />
            </Routes>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
